import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useUser } from "@clerk/react";
import { ComposableMap, Geographies, Geography, Annotation } from "react-simple-maps";
import { useSubscription } from "../context/SubscriptionContext";
import { apiFetch } from "../utils/apiClient";
import {
  formatCurrency,
  getListGeneralByYear,
  getListTopProviders,
  getListAllCompanies,
  getListGeneralStateBreakdown,
  getListTopProducts,
  getListOwnershipSummary,
  getListOwnershipDetail,
  getListAllOwnershipCompanies,
  getListOwnershipStateBreakdown,
  getListResearchSummary,
  getListAllResearchCompanies,
  getListResearchProviders,
  getListResearchProducts,
  getListCompanyTrend,
  getListOwnershipTrend,
  getListResearchTrend,
  checkPodBatch,
  checkDualInterestBatch,
  checkExclusionsBatch,
} from "../utils/cmsApi";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
const DEFAULT_YEAR = 2025;

// ── Helpers ───────────────────────────────────────────────────────────────────
function shortSpecialty(raw) {
  if (!raw) return "";
  const parts = raw.split("|");
  return parts[parts.length - 1].trim();
}

function formatCompact(value) {
  if (!value || value === 0) return "$0";
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

// ── Interest type categorization — matches PhysicianOwnersPage.categorize() ──
function categorize(terms) {
  if (!terms) return "Other";
  const t = terms.toUpperCase();
  if (t.includes("COMMON") && t.includes("PREFERRED")) return "Common & Preferred Stock";
  if (t.includes("RESTRICTED STOCK") || t.includes("RSU") || t.includes("PRSU")
    || t.includes("DEFERRED STOCK UNIT")) return "Restricted Stock / RSUs";
  if (t.includes("STOCK OPTION") || t.includes("OPTION TO PURCHASE")
    || t.includes("STOCK AND OR OPTIONS") || t.includes("STOCK AND/OR OPTIONS")
    || t.includes("OPTION GRANT") || t.includes("OPTIONS GRANTED")
    || t.includes("OPTIONS EXERCISED") || t.includes("EXERCISED OPTION")
    || t.includes("OPTIONS FOR SERVICES") || t.includes("OPTION FOR COMMON")
    || t.includes("NSO GRANT") || t.includes("VESTED OPTION")
    || t.includes("UNEXERCISED OPTION") || t.includes("BLACK-SCHOLES")
    || t.includes("BLACK SCHOLES") || t.includes("ADVISOR OPTION")
    || (t.includes("OPTION") && t.includes("EXERCISE"))) return "Stock Options";
  if (t.includes("WARRANT")) return "Warrants";
  if (t.includes("COMMON STOCK") || t.includes("COMMON SHARE")
    || t.includes("COMMON UNIT") || t.includes("ORDINARY SHARE")
    || t.includes("FOUNDER") || t === "COMMON"
    || t.includes("SHARES OF STOCK") || t.includes("COMPANY STOCK")
    || t.includes("STOCK OWNERSHIP") || t.includes("STOCK HOLDER")
    || t.includes("STOCKHOLDER") || t.includes("SHARE OWNERSHIP")
    || t.includes("STOCK GRANT") || t.includes("STOCK PURCHASE")
    || t.includes("PURCHASED CORPORATE SHARE") || t.includes("EQUITY STOCK")
    || t.includes("SHARES OF THE COMPANY") || t.includes("SHARES OF COMPANY")
    || t.includes("STOCK INTEREST")) return "Common Stock";
  if (t.includes("PREFERRED") || t.includes("SERIES A") || t.includes("SERIES B")
    || t.includes("SERIES C") || t.includes("SERIES D") || t.includes("SERIES E")
    || t.includes("SERIES F") || t.includes("SERIES SEED")) return "Preferred Stock";
  if (t.includes("LLC") || t.includes("L.L.C") || t.includes("MEMBERSHIP INTEREST")
    || t.includes("MEMBERSHIP UNIT") || t.includes("PROFIT INTEREST")
    || t.includes("PROFITS INTEREST") || t.includes("OPERATING AGREEMENT")
    || t.includes("CLASS A UNIT") || t.includes("CLASS B UNIT")
    || t.includes("CLASS C UNIT") || t.includes("CLASS D UNIT")
    || t.includes("B UNITS") || t.includes("A UNITS")
    || t.includes("UNIT HOLDER") || t.includes("UNITHOLDER")) return "LLC / Membership Interest";
  if (t.includes("CONVERTIBLE") || t.includes("SAFE INVESTMENT")
    || t.includes("SAFE 20%") || t.includes("PROMISSORY NOTE")
    || t === "SAFE") return "Convertible Instruments";
  if (t.includes("PARTNERSHIP") || t.includes("LP ") || t.includes("LLP")
    || t.includes("CAPITAL CONTRIBUTION") || t.includes("CAPITAL INVESTMENT")
    || t.includes("PARTNER CAPITAL") || t.includes("PARTNERSHIP SHARES")) return "Partnership Interest";
  if (t.includes("ACQUISITION") || t.includes("LIQUIDAT") || t.includes("REPURCHAS")
    || t.includes("BOUGHT BACK") || t.includes("BUYOUT") || t.includes("MERGER")
    || t.includes("COMPANY SOLD") || t.includes("ASSETS SOLD")
    || t.includes("CEASED BUSINESS") || t.includes("DISSOLVED")
    || t.includes("BANKRUPTCY") || t.includes("OUT OF BUSINESS")) return "Acquisition / Liquidation";
  if (t.includes("ROYALT") || t.includes("DIVIDEND") || t.includes("DISTRIBUTION")
    || t.includes("10% OF NET PROFIT") || t.includes("PROFIT BASED ON")
    || t.includes("20% RETURN") || t.includes("20% PROFIT")) return "Royalties / Distributions";
  if (t.includes("C-CORP") || t.includes("C CORP") || t.includes("S-CORP") || t.includes("S CORP")
    || t.includes("PRIVATELY HELD") || t.includes("NON-MARKETABLE") || t.includes("NON MARKETABLE")
    || t.includes("SHAREHOLDER") || t.includes("PRIVATE OWNERSHIP")) return "C-Corp / Private Equity";
  if (t === "EQUITY" || t === "INVESTMENT" || t === "STOCK" || t === "SHARES" || t === "SHARES OF EQUITY"
    || t === "OWNERSHIP" || t === "INVESTOR" || t.includes("EQUITY INVESTMENT")
    || t.includes("EQUITY INTEREST") || t.includes("EQUITY OWNERSHIP")
    || t === "STOCK OWNERSHIP" || t === "PURCHASED SHARES"
    || t.includes("CASH INVESTMENT") || t.includes("PRIVATE PLACEMENT")
    || t.includes("OWNERSHIP INTEREST")) return "Equity Investment";
  return "Other";
}

function formatNumber(n) {
  return (n || 0).toLocaleString();
}

function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

function yearLabel(year) {
  return year === "all" || !year ? "All Years" : year;
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ value, label, color = "text-white", sub }) {
  return (
    <div className="card py-3 px-4 text-center">
      <div className={`font-display text-2xl ${color}`}>{value}</div>
      <div className="text-white/30 text-xs mt-0.5">{label}</div>
      {sub && <div className="text-white/20 text-xs mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Generic export button ─────────────────────────────────────────────────────
function ExportButton({ onClick }) {
  return (
    <button onClick={onClick} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">
      ⬇ Export CSV
    </button>
  );
}

// ── Geographic map config (shared by General + Ownership maps) ────────────────
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STATE_CENTROIDS = {
  AL:{lon:-86.8,lat:32.8},AK:{lon:-153.4,lat:61.4},AZ:{lon:-111.7,lat:34.3},
  AR:{lon:-92.4,lat:34.9},CA:{lon:-119.7,lat:37.2},CO:{lon:-105.5,lat:39.0},
  CT:{lon:-72.7,lat:41.6},DE:{lon:-75.5,lat:39.0},FL:{lon:-81.5,lat:28.5},
  GA:{lon:-83.4,lat:32.7},HI:{lon:-157.8,lat:20.3},ID:{lon:-114.7,lat:44.4},
  IL:{lon:-89.2,lat:40.0},IN:{lon:-86.3,lat:39.9},IA:{lon:-93.5,lat:42.1},
  KS:{lon:-98.4,lat:38.5},KY:{lon:-84.9,lat:37.5},LA:{lon:-91.8,lat:31.2},
  ME:{lon:-69.2,lat:45.4},MD:{lon:-76.6,lat:39.1},MA:{lon:-71.5,lat:42.3},
  MI:{lon:-85.4,lat:44.3},MN:{lon:-94.3,lat:46.4},MS:{lon:-89.7,lat:32.7},
  MO:{lon:-92.3,lat:38.4},MT:{lon:-110.4,lat:47.0},NE:{lon:-99.9,lat:41.5},
  NV:{lon:-116.4,lat:39.3},NH:{lon:-71.6,lat:43.7},NJ:{lon:-74.5,lat:40.1},
  NM:{lon:-106.1,lat:34.5},NY:{lon:-75.5,lat:43.0},NC:{lon:-79.4,lat:35.6},
  ND:{lon:-100.5,lat:47.5},OH:{lon:-82.8,lat:40.4},OK:{lon:-97.5,lat:35.5},
  OR:{lon:-120.5,lat:44.1},PA:{lon:-77.2,lat:40.9},RI:{lon:-71.5,lat:41.7},
  SC:{lon:-80.9,lat:33.8},SD:{lon:-100.2,lat:44.4},TN:{lon:-86.3,lat:35.9},
  TX:{lon:-99.3,lat:31.5},UT:{lon:-111.1,lat:39.4},VT:{lon:-72.7,lat:44.0},
  VA:{lon:-78.7,lat:37.5},WA:{lon:-120.5,lat:47.4},WV:{lon:-80.6,lat:38.6},
  WI:{lon:-89.6,lat:44.6},WY:{lon:-107.6,lat:43.0},DC:{lon:-77.0,lat:38.9},
};
const LABEL_OFFSETS = {
  CT:{dx:15,dy:-8},DE:{dx:18,dy:5},DC:{dx:18,dy:5},MA:{dx:18,dy:-8},
  MD:{dx:18,dy:8},NH:{dx:15,dy:-8},NJ:{dx:18,dy:5},RI:{dx:18,dy:5},
  VT:{dx:15,dy:-8},
};
const FIPS_TO_ABBR = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT",
  "10":"DE","11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL",
  "18":"IN","19":"IA","20":"KS","21":"KY","22":"LA","23":"ME","24":"MD",
  "25":"MA","26":"MI","27":"MN","28":"MS","29":"MO","30":"MT","31":"NE",
  "32":"NV","33":"NH","34":"NJ","35":"NM","36":"NY","37":"NC","38":"ND",
  "39":"OH","40":"OK","41":"OR","42":"PA","44":"RI","45":"SC","46":"SD",
  "47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA","54":"WV",
  "55":"WI","56":"WY",
};

// ── Reusable choropleth map ───────────────────────────────────────────────────
// stateData: array of { state, ...metrics }; valueField: which numeric field maps to color
// colorRgb: "251,191,36" (gold) for General, or "245,158,11" (amber) for Ownership
function ChoroplethMap({ stateData, valueField, colorRgb, hoverRender, emptyText }) {
  const [hoveredState, setHoveredState] = useState(null);

  const stateMap = {};
  stateData.forEach((s) => { if (s.state) stateMap[s.state] = s; });
  const maxVal = Math.max(...stateData.map((s) => s[valueField] || 0), 1);

  function getFill(abbr) {
    const s = stateMap[abbr];
    if (!s || !s[valueField]) return "rgba(255,255,255,0.05)";
    const i = s[valueField] / maxVal;
    if (i > 0.75) return `rgba(${colorRgb},0.92)`;
    if (i > 0.50) return `rgba(${colorRgb},0.68)`;
    if (i > 0.25) return `rgba(${colorRgb},0.40)`;
    if (i > 0.08) return `rgba(${colorRgb},0.20)`;
    return `rgba(${colorRgb},0.08)`;
  }

  const hovered = hoveredState ? stateMap[hoveredState] : null;

  if (!stateData.length) {
    return <p className="text-white/30 text-sm text-center py-8">{emptyText || "No geographic data available."}</p>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="print:hidden text-white/30 text-xs">{hovered ? null : "hover a state"}</div>
        {hovered && hoverRender && (
          <div className="text-right">{hoverRender(hoveredState, hovered)}</div>
        )}
      </div>
      <div className="w-full rounded-lg overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", marginLeft: "-8px", marginRight: "-8px", width: "calc(100% + 16px)" }}>
        <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto", display: "block" }}
          projectionConfig={{ scale: 1020, translate: [400, 240] }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const fips = geo.id?.toString().padStart(2, "0");
                const abbr = FIPS_TO_ABBR[fips];
                const isHovered = hoveredState === abbr;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={getFill(abbr)}
                    stroke={isHovered ? `rgba(${colorRgb},0.8)` : "rgba(255,255,255,0.10)"}
                    strokeWidth={isHovered ? 1.5 : 0.5}
                    style={{
                      default: { outline: "none", cursor: stateMap[abbr] ? "pointer" : "default", transition: "fill 0.15s" },
                      hover: { outline: "none" }, pressed: { outline: "none" },
                    }}
                    onMouseEnter={() => abbr && setHoveredState(abbr)}
                    onMouseLeave={() => setHoveredState(null)}
                  />
                );
              })
            }
          </Geographies>
          {Object.entries(STATE_CENTROIDS).map(([abbr, { lon, lat }]) => {
            const s = stateMap[abbr];
            const intensity = s ? s[valueField] / maxVal : 0;
            const offset = LABEL_OFFSETS[abbr];
            const dx = offset?.dx || 0;
            const dy = offset?.dy || 0;
            const textColor = intensity > 0.5 ? "rgba(0,0,0,0.75)" : "rgba(255,255,255,0.55)";
            return (
              <Annotation key={abbr} subject={[lon, lat]} dx={dx} dy={dy}
                connectorProps={offset ? { stroke: "rgba(255,255,255,0.25)", strokeWidth: 0.5, strokeLinecap: "round" } : {}}>
                <text textAnchor="middle" alignmentBaseline="middle"
                  style={{ fontSize: abbr === "DC" ? "5px" : "7px", fontWeight: 600, fill: textColor,
                    pointerEvents: "none", userSelect: "none", fontFamily: "ui-monospace, monospace" }}>
                  {abbr}
                </text>
              </Annotation>
            );
          })}
        </ComposableMap>
      </div>
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
        <span className="text-white/20 text-xs shrink-0">More</span>
        <div className="flex gap-0.5 flex-1">
          {[0.92, 0.68, 0.40, 0.20, 0.08].map((v) => (
            <div key={v} className="flex-1 h-2 rounded-sm" style={{ backgroundColor: `rgba(${colorRgb},${v})` }} />
          ))}
        </div>
        <span className="text-white/20 text-xs shrink-0">Less</span>
      </div>
    </>
  );
}

// ── Payment trend stacked bar ─────────────────────────────────────────────────
function PaymentTrendChart({ trend, selectedYear, onYearClick, listName }) {
  const [hoveredYear, setHoveredYear] = useState(null);
  if (!trend || trend.length === 0) return null;

  // Top 8 sort is year-aware: when a specific year is selected, rank by THAT
  // year's total so the chart matches the year-specific Top Companies table.
  // When "All years" is selected, fall back to grand total across all 9 years.
  const isAllYears = selectedYear === "all";
  const allSorted = [...trend].sort((a, b) => {
    if (isAllYears) return (b.grandTotal || 0) - (a.grandTotal || 0);
    const aYear = (a.byYear || []).find((r) => r.year === selectedYear)?.total || 0;
    const bYear = (b.byYear || []).find((r) => r.year === selectedYear)?.total || 0;
    return bYear - aYear;
  });
  const top8 = allSorted.slice(0, 8);
  const rest = allSorted.slice(8);
  const COLORS = [
    "rgba(251,191,36,0.8)", "rgba(139,92,246,0.8)", "rgba(20,184,166,0.8)",
    "rgba(59,130,246,0.8)", "rgba(239,68,68,0.8)", "rgba(249,115,22,0.8)",
    "rgba(34,197,94,0.8)", "rgba(236,72,153,0.8)",
  ];
  const OTHER_COLOR = "rgba(100,116,139,0.6)";
  const colorMap = new Map(top8.map((c, i) => [c.companyName, COLORS[i]]));

  // Build segments for rendering (top 8 only) and track Other separately for the tooltip
  const yearStacks = ALL_YEARS.map((y) => {
    let visibleTotal = 0;
    const segments = top8.map((company) => {
      const row = (company.byYear || []).find((r) => r.year === y);
      const val = row ? (row.total || 0) : 0;
      visibleTotal += val;
      return { company: company.companyName, value: val };
    });
    let otherTotal = 0;
    rest.forEach((c) => {
      const row = (c.byYear || []).find((r) => r.year === y);
      otherTotal += row ? (row.total || 0) : 0;
    });
    return {
      year: y,
      segments,            // top 8 only — used for rendering
      visibleTotal,        // sum of top 8 — drives bar height
      otherTotal,          // hidden in chart, shown in tooltip
      grandTotal: visibleTotal + otherTotal,
    };
  });
  const maxTotal = Math.max(...yearStacks.map((y) => y.grandTotal), 1);

  function exportTrend() {
    // Pivot: one row per company across ALL companies (not just top 8), with
    // company_name and company_id in columns 1-2, then one column per year, then grand total.
    const yearTotals = {};
    ALL_YEARS.forEach((y) => (yearTotals[y] = 0));

    const rows = [
      ["Company Name", "Company ID", ...ALL_YEARS.map((y) => String(y)), "Grand Total"],
    ];

    // Sort all companies by grand total descending
    const sortedCompanies = [...trend].sort((a, b) => (b.grandTotal || 0) - (a.grandTotal || 0));

    sortedCompanies.forEach((c) => {
      const yearMap = {};
      ALL_YEARS.forEach((y) => (yearMap[y] = 0));
      (c.byYear || []).forEach((r) => {
        if (yearMap[r.year] !== undefined) {
          yearMap[r.year] = r.total || 0;
          yearTotals[r.year] += r.total || 0;
        }
      });
      rows.push([
        c.companyName,
        c.companyId ? `'${c.companyId}` : "",
        ...ALL_YEARS.map((y) => yearMap[y].toFixed(2)),
        (c.grandTotal || 0).toFixed(2),
      ]);
    });

    // Footer row: column totals
    const grandSum = Object.values(yearTotals).reduce((s, v) => s + v, 0);
    rows.push([
      "TOTAL",
      "",
      ...ALL_YEARS.map((y) => yearTotals[y].toFixed(2)),
      grandSum.toFixed(2),
    ]);

    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_payment_trend_by_company.csv`);
  }

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Payment trend by company</h2>
        <div className="flex items-center gap-3">
          <p className="text-white/25 text-xs">
            Top 8 by {isAllYears ? "all-years total" : `${selectedYear} payments`} + Other · click year to filter
          </p>
          <ExportButton onClick={exportTrend} />
        </div>
      </div>
      <div className="relative" style={{ height: "200px" }}>
        <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8" style={{ left: "30px" }}>
          {yearStacks.map(({ year, segments, visibleTotal, otherTotal, grandTotal }) => {
                const heightPct = (grandTotal / maxTotal) * 100;
                const isSelected = selectedYear === year;
                return (
                  <div key={year} className="flex-1 flex flex-col items-center justify-end h-full group relative cursor-pointer"
                    onClick={() => setSelectedYear(year)}>
                    {grandTotal > 0 && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none" style={{ minWidth: "200px" }}>
                        <p className="text-teal-400 font-medium mb-1.5 pb-1.5 border-b border-white/10">{year} · {formatCompact(grandTotal)}</p>
                        {segments.filter((s) => s.value > 0).sort((a, b) => b.value - a.value).map(({ company, value }) => {
                          const pct = grandTotal > 0 ? (value / grandTotal) * 100 : 0;
                          return (
                            <div key={company} className="flex items-center justify-between gap-2 mt-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: colorMap.get(company) }} />
                                <span className="text-white/40 truncate" style={{ maxWidth: "100px" }}>{company.length > 18 ? company.slice(0, 18) + "…" : company}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-white/25" style={{ minWidth: "32px", textAlign: "right" }}>{pct.toFixed(1)}%</span>
                                <span className="text-white/60" style={{ minWidth: "48px", textAlign: "right" }}>{formatCompact(value)}</span>
                              </div>
                            </div>
                          );
                        })}
                        {otherTotal > 0 && (
                          <div className="flex items-center justify-between gap-2 mt-1 pt-1.5 border-t border-white/5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: OTHER_COLOR }} />
                              <span className="text-white/40 italic">Other ({rest.length})</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-white/25" style={{ minWidth: "32px", textAlign: "right" }}>{(otherTotal / grandTotal * 100).toFixed(1)}%</span>
                              <span className="text-white/60" style={{ minWidth: "48px", textAlign: "right" }}>{formatCompact(otherTotal)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {grandTotal > 0 && (
                      <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "3px" }}>
                        <span className="text-teal-400/70 font-medium" style={{ fontSize: "9px" }}>{formatCompact(grandTotal)}</span>
                      </div>
                    )}
                    <div className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                      style={{
                        height: grandTotal > 0 ? `${Math.max(heightPct, 3)}%` : "3px",
                        outline: isSelected ? "2px solid rgba(20,184,166,0.8)" : "none",
                        outlineOffset: "2px",
                      }}>
                      {/* Other rendered first → appears at the bottom of the bar (flex-col-reverse) */}
                      {otherTotal > 0 && (
                        <div style={{ height: `${(otherTotal / grandTotal) * 100}%`, backgroundColor: OTHER_COLOR, minHeight: "2px" }} />
                      )}
                      {grandTotal > 0 && segments.map(({ company, value }) =>
                        value > 0 ? <div key={company} style={{ height: `${(value / grandTotal) * 100}%`, backgroundColor: colorMap.get(company), minHeight: "2px" }} /> : null
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
        <div className="absolute bottom-0 left-0 right-0 flex gap-1.5" style={{ paddingLeft: "32px" }}>
          {ALL_YEARS.map((y) => (
            <div key={y} className="flex-1 text-center">
              <span className={`${y === selectedYear ? "text-gold-400" : y === hoveredYear ? "text-white/60" : "text-white/25"}`} style={{ fontSize: "10px" }}>{y}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
        {top8.map((c, i) => (
          <div key={c.companyName} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: COLORS[i], opacity: 0.8 }} />
            <span className="text-white/40 text-xs truncate max-w-32">{c.companyName.length > 22 ? c.companyName.slice(0, 22) + "…" : c.companyName}</span>
          </div>
        ))}
        {rest.length > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: OTHER_COLOR }} />
            <span className="text-white/40 text-xs">Other ({rest.length} companies, hover to see)</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Specialty breakdown — sorted descending, top 3 ────────────────────────────
function SpecialtyBreakdown({ specialties, year, listName }) {
  if (!specialties || specialties.length === 0) return null;
  const sorted = [...specialties].sort((a, b) => (b.total || 0) - (a.total || 0));
  const top3 = sorted.slice(0, 3);
  const max = Math.max(...sorted.map((s) => s.total || 0), 1);

  function exportSpecialties() {
    const rows = [["Rank", "Specialty", "Provider Count", "Total Payments ($)"]];
    sorted.forEach((s, i) => {
      rows.push([i + 1, shortSpecialty(s.specialty), s.cnt || 0, (s.total || 0).toFixed(2)]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_specialty_breakdown_${year}.csv`);
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Specialty breakdown ({yearLabel(year)})</h2>
        <ExportButton onClick={exportSpecialties} />
      </div>
      <div className="space-y-3">
        {top3.map((s, i) => {
          const pct = ((s.total || 0) / max) * 100;
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/60 text-xs truncate max-w-xs">{shortSpecialty(s.specialty)}</span>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/30 text-xs">{formatNumber(s.cnt)} providers</span>
                  <span className="text-gold-400/80 text-xs font-mono w-20 text-right">{formatCurrency(s.total || 0)}</span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div className="h-2 rounded-full bg-gold-400/60" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {sorted.length > 3 && (
        <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
          Showing top 3 of {formatNumber(sorted.length)} specialties · export for full list
        </p>
      )}
    </div>
  );
}

// ── Top products card — companion to SpecialtyBreakdown (side-by-side) ────────
const PRODUCT_TYPE_BAR_COLORS = {
  "Drug": "bg-purple-500/70",
  "Biological": "bg-teal-500/70",
  "Device": "bg-blue-500/70",
  "Medical Supply": "bg-green-500/70",
};
const PRODUCT_TYPE_BADGE_COLORS = {
  "Drug": "bg-purple-500/15 border-purple-500/30 text-purple-300",
  "Biological": "bg-teal-500/15 border-teal-500/30 text-teal-300",
  "Device": "bg-blue-500/15 border-blue-500/30 text-blue-300",
  "Medical Supply": "bg-green-500/15 border-green-500/30 text-green-300",
};

function TopProductsCard({ products, year, listName }) {
  if (!products || products.length === 0) {
    return (
      <div className="card">
        <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">Top products ({yearLabel(year)})</h2>
        <p className="text-white/30 text-sm text-center py-6">No product-level data for this period.</p>
      </div>
    );
  }
  const sorted = [...products].sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const top3 = sorted.slice(0, 3);
  const max = Math.max(...sorted.map((s) => s.amount || 0), 1);
  const total = sorted.reduce((s, p) => s + (p.amount || 0), 0);

  function exportProducts() {
    const rows = [["Rank", "Product", "Product Type", "Payment Count", "Amount ($)", "% of Total"]];
    sorted.forEach((p, i) => {
      rows.push([
        i + 1, p.productName, p.productType,
        p.paymentCount || 0,
        (p.amount || 0).toFixed(2),
        total > 0 ? ((p.amount / total) * 100).toFixed(1) + "%" : "0.0%",
      ]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_top_products_${year}.csv`);
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Top products ({yearLabel(year)})</h2>
        <ExportButton onClick={exportProducts} />
      </div>
      <div className="space-y-3">
        {top3.map((p, i) => {
          const pct = ((p.amount || 0) / max) * 100;
          const barClass = PRODUCT_TYPE_BAR_COLORS[p.productType] || "bg-white/20";
          const badgeClass = PRODUCT_TYPE_BADGE_COLORS[p.productType] || "bg-white/10 border-white/20 text-white/40";
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-white/60 text-xs truncate">{p.productName}</span>
                  {p.productType && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${badgeClass}`} style={{ fontSize: "10px" }}>
                      {p.productType}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/30 text-xs">{formatNumber(p.paymentCount)} payments</span>
                  <span className="text-gold-400/80 text-xs font-mono w-20 text-right">{formatCurrency(p.amount || 0)}</span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div className={`h-2 rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {sorted.length > 3 && (
        <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
          Showing top 3 of {formatNumber(sorted.length)} products · export for full list
        </p>
      )}
    </div>
  );
}

// ── Top providers card list (General tab) ─────────────────────────────────────
// Conflict pills next to name, payment-type pills at the bottom (per screenshot)
function TopProvidersCardList({ providers, year, listName }) {
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [minPayments, setMinPayments] = useState("");
  const [exclusionFilter, setExclusionFilter] = useState("any");
  const [conflictFilter, setConflictFilter] = useState("any");
  const [podFilter, setPodFilter] = useState("any");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const uniqueStates = useMemo(() => {
    const set = new Set();
    providers.forEach((p) => p.state && set.add(p.state));
    return [...set].sort();
  }, [providers]);

  const uniqueSpecialties = useMemo(() => {
    const set = new Set();
    providers.forEach((p) => p.specialty && set.add(shortSpecialty(p.specialty)));
    return [...set].sort();
  }, [providers]);

  const filtered = useMemo(() => {
    return providers.filter((p) => {
      const fullName = `${p.firstName} ${p.lastName}`.trim().toLowerCase();
      const npi = String(p.npi || "");
      if (search) {
        const q = search.toLowerCase();
        if (!fullName.includes(q) && !npi.includes(q)) return false;
      }
      if (stateFilter && p.state !== stateFilter) return false;
      if (specialtyFilter && shortSpecialty(p.specialty) !== specialtyFilter) return false;
      const min = parseFloat(minPayments) || 0;
      if (min > 0 && (p.totalPayments || 0) < min) return false;
      if (exclusionFilter === "excluded" && !p.isExcluded) return false;
      if (exclusionFilter === "clean" && p.isExcluded) return false;
      if (conflictFilter === "triple" && !p.hasTripleInterest) return false;
      if (conflictFilter === "dual" && !p.hasDualInterest) return false;
      if (conflictFilter === "none" && (p.hasDualInterest || p.hasTripleInterest)) return false;
      if (podFilter === "yes" && !p.hasPodRisk) return false;
      if (podFilter === "no" && p.hasPodRisk) return false;
      return true;
    });
  }, [providers, search, stateFilter, specialtyFilter, minPayments, exclusionFilter, conflictFilter, podFilter]);

  function exportProviders() {
    const rows = [["Rank", "NPI", "Name", "Specialty", "City", "State", "Total Payments ($)", "Payment Count",
      "Dual Interest", "Triple Interest", "Ownership", "Research", "Excluded", "POD Risk (OIG Work Plan 2026)"]];
    filtered.forEach((p, i) => {
      rows.push([
        i + 1, p.npi, `${p.firstName} ${p.lastName}`.trim(), p.specialty, p.city, p.state,
        (p.totalPayments || 0).toFixed(2), p.paymentCount || 0,
        p.hasDualInterest ? "Yes" : "No", p.hasTripleInterest ? "Yes" : "No",
        p.hasOwnership ? "Yes" : "No", p.hasResearch ? "Yes" : "No",
        p.isExcluded ? "Yes" : "No",
        p.hasPodRisk ? (p.podTier ? `Tier ${p.podTier}` : "Tier 1") : "No",
      ]);
    });
    const suffix = hasFilters ? "_filtered" : "";
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_top_providers_${year}${suffix}.csv`);
  }

  function resetFilters() {
    setSearch(""); setStateFilter(""); setSpecialtyFilter("");
    setMinPayments(""); setExclusionFilter("any"); setConflictFilter("any");
    setPodFilter("any");
  }

  const hasFilters = search || stateFilter || specialtyFilter || minPayments ||
    exclusionFilter !== "any" || conflictFilter !== "any" || podFilter !== "any";

  if (!providers || providers.length === 0) {
    return (
      <div className="card mb-6">
        <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">Top providers by payments ({yearLabel(year)})</h2>
        <p className="text-white/30 text-sm text-center py-6">No payment data found for this year.</p>
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Top providers by payments ({yearLabel(year)})</h2>
        <ExportButton onClick={exportProviders} />
      </div>

      <div className="print:hidden flex items-center gap-2 mb-3">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or NPI..."
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-gold-500/50" />
        <button onClick={() => setFiltersOpen(!filtersOpen)}
          className={`text-xs px-3 py-2 rounded-lg border transition-all ${
            filtersOpen || hasFilters ? "border-gold-500/40 text-gold-400 bg-gold-500/10" : "border-white/10 text-white/40 hover:border-white/20"
          }`}>
          ⚙ Filters {hasFilters && "•"}
        </button>
        {hasFilters && <button onClick={resetFilters} className="text-white/30 hover:text-white/60 text-xs">Clear</button>}
      </div>

      {filtersOpen && (
        <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 mb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
          <div>
            <label className="text-white/40 text-xs mb-1 block">State</label>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50">
              <option value="">Any</option>
              {uniqueStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Specialty</label>
            <select value={specialtyFilter} onChange={(e) => setSpecialtyFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50">
              <option value="">Any</option>
              {uniqueSpecialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Min total payments ($)</label>
            <input type="number" value={minPayments} onChange={(e) => setMinPayments(e.target.value)}
              placeholder="e.g. 10000"
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50" />
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Exclusion</label>
            <select value={exclusionFilter} onChange={(e) => setExclusionFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50">
              <option value="any">Any</option>
              <option value="excluded">Excluded only</option>
              <option value="clean">Not excluded</option>
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Conflict</label>
            <select value={conflictFilter} onChange={(e) => setConflictFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50">
              <option value="any">Any</option>
              <option value="triple">Triple Interest</option>
              <option value="dual">Dual or Triple</option>
              <option value="none">No conflict</option>
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">POD Risk</label>
            <select value={podFilter} onChange={(e) => setPodFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-gold-500/50">
              <option value="any">Any</option>
              <option value="yes">POD Risk only</option>
              <option value="no">No POD Risk</option>
            </select>
          </div>
        </div>
      )}

      <p className="print:hidden text-white/30 text-xs mb-3">
        Showing {Math.min(filtered.length, 10)} of {formatNumber(filtered.length)} provider{filtered.length !== 1 ? "s" : ""}
        {hasFilters && providers.length !== filtered.length && (
          <span className="text-white/20"> · filtered from {formatNumber(providers.length)}</span>
        )}
      </p>

      {/* Print-only top-5 providers */}
      <div className="hidden print:block mb-4">
        <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
          Top Providers · {yearLabel(year)}
        </h2>
        <div className="space-y-2">
          {providers.slice(0, 5).map((p, i) => (
            <div key={p.npi} className="border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-gray-900 text-sm font-medium">{`${p.firstName} ${p.lastName}`.trim()}</p>
                    {p.hasTripleInterest && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                    {!p.hasTripleInterest && p.hasDualInterest && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                    {p.isExcluded && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Excluded</span>}
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5">
                    {p.specialty && <span>{shortSpecialty(p.specialty)} · </span>}
                    {p.city && <span>{p.city}{p.state ? `, ${p.state}` : ""} · </span>}
                    NPI: {p.npi}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(p.totalPayments || 0)}</p>
                  <p className="text-gray-400 text-xs">{formatNumber(p.paymentCount)} payments</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {providers.length > 5 && (
          <p className="text-gray-400 text-xs mt-2">Showing 5 of {formatNumber(providers.length)} providers · export full list from ProviderPayWatch</p>
        )}
      </div>

      <div className="print:hidden space-y-2">
        {filtered.slice(0, 10).map((p, i) => (
          <div key={p.npi} className={`rounded-lg border px-3 py-2.5 ${
            p.hasTripleInterest ? "bg-red-500/5 border-red-500/20"
            : p.hasDualInterest ? "bg-orange-500/5 border-orange-500/20"
            : p.isExcluded ? "bg-red-500/[0.03] border-red-500/15"
            : "bg-white/[0.02] border-white/[0.06]"
          }`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                {/* Name + conflict pills inline */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white/30 text-xs w-5 shrink-0">{i + 1}.</span>
                  <Link to={`/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName} ${p.lastName}`.trim())}&state=${encodeURIComponent(p.state || "")}`}
                    className="text-white/80 text-sm font-medium hover:text-gold-300 transition-colors">
                    {`${p.firstName} ${p.lastName}`.trim() || p.npi}
                  </Link>
                  {p.hasTripleInterest && (
                    <span className="bg-red-500/15 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚠ Triple Interest</span>
                  )}
                  {!p.hasTripleInterest && p.hasDualInterest && (
                    <span className="bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚑ Dual Interest</span>
                  )}
                  {p.hasPodRisk && (
                    <span className="bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                      ⚠ POD Risk Tier {p.podTier || 1}
                    </span>
                  )}
                  {p.isExcluded && (
                    <span className="bg-red-500/15 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚠ Excluded</span>
                  )}
                </div>
                <p className="text-white/40 text-xs mt-1">
                  {p.specialty && <span>{shortSpecialty(p.specialty)} · </span>}
                  {p.city && <span>{p.city}{p.state ? `, ${p.state}` : ""}</span>}
                  {!p.city && p.state && <span>{p.state}</span>}
                </p>
                <p className="text-white/25 text-xs font-mono mt-0.5">NPI: {p.npi}</p>

                {/* Payment-type pills at the bottom */}
                {(p.hasOwnership || p.hasResearch || (p.totalPayments || 0) > 0) && (
                  <div className="flex gap-1.5 flex-wrap mt-2">
                    {(p.totalPayments || 0) > 0 && (
                      <span className="bg-gold-500/10 border border-gold-500/25 text-gold-400/90 text-xs px-1.5 py-0.5 rounded-full">💊 Payments</span>
                    )}
                    {p.hasOwnership && (
                      <span className="bg-amber-500/10 border border-amber-500/25 text-amber-400/90 text-xs px-1.5 py-0.5 rounded-full">🏦 Ownership</span>
                    )}
                    {p.hasResearch && (
                      <span className="bg-teal-500/10 border border-teal-500/25 text-teal-400/90 text-xs px-1.5 py-0.5 rounded-full">🔬 Research</span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-gold-400 text-sm font-mono font-medium">{formatCurrency(p.totalPayments || 0)}</p>
                <p className="text-white/25 text-xs">{formatNumber(p.paymentCount)} payments</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length > 10 && (
        <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
          Showing top 10 of {formatNumber(filtered.length)} providers · export for full list
        </p>
      )}
      {filtered.length === 0 && (
        <p className="text-white/30 text-xs text-center py-4">No providers match the current filters.</p>
      )}
    </div>
  );
}

// ── Top companies table ───────────────────────────────────────────────────────
function TopCompaniesTable({ companies, listName, year }) {
  if (!companies || companies.length === 0) return null;

  function exportCompanies() {
    const rows = [["Rank", "Company", "Company ID", "Total Payments ($)", "Provider Count", "Payment Count"]];
    companies.forEach((c, i) => {
      rows.push([i + 1, c.companyName, c.companyId ? `'${c.companyId}` : "",
        (c.totalPayments || c.total || 0).toFixed(2),
        c.providerCount || 0, c.paymentCount || 0]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_companies_${year}.csv`);
  }

  const max = Math.max(...companies.map((c) => c.totalPayments || c.total || 0), 1);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Top companies paying list members ({yearLabel(year)})</h2>
        <ExportButton onClick={exportCompanies} />
      </div>
      <div className="space-y-3">
        {companies.slice(0, 10).map((c, i) => {
          const value = c.totalPayments || c.total || 0;
          const pct = (value / max) * 100;
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-white/20 text-xs w-5 shrink-0">{i + 1}</span>
                  {c.companyId ? (
                    <Link to={`/company/${c.companyId}`}
                      className="text-white/70 hover:text-gold-300 text-xs truncate transition-colors">
                      {c.companyName}
                    </Link>
                  ) : (
                    <span className="text-white/70 text-xs truncate">{c.companyName}</span>
                  )}
                  <span className="text-white/25 text-xs shrink-0">{formatNumber(c.providerCount)} providers</span>
                </div>
                <span className="text-gold-400 text-xs font-mono shrink-0">{formatCurrency(value)}</span>
              </div>
              <div className="w-full bg-white/5 rounded-full h-1.5">
                <div className="h-1.5 rounded-full bg-gold-400/50" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {companies.length > 10 && (
        <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
          Showing top 10 of {formatNumber(companies.length)} companies · export for full list
        </p>
      )}
    </div>
  );
}

// ── General Geographic Map ────────────────────────────────────────────────────
function GeneralGeographicMap({ stateData, year, listName }) {
  if (!stateData || stateData.length === 0) return null;

  function exportStates() {
    const rows = [["State", "Total Paid ($)", "Provider Count", "Payment Count", "Avg per Provider ($)"]];
    [...stateData].sort((a, b) => b.totalPaid - a.totalPaid).forEach((s) => {
      const avg = s.providerCount > 0 ? (s.totalPaid / s.providerCount).toFixed(2) : "0.00";
      rows.push([s.state, s.totalPaid.toFixed(2), s.providerCount, s.paymentCount, avg]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_geographic_general_${year}.csv`);
  }

  const top5 = [...stateData].sort((a, b) => b.totalPaid - a.totalPaid).slice(0, 5);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Geographic payment distribution ({yearLabel(year)})
        </h2>
        <ExportButton onClick={exportStates} />
      </div>
      <ChoroplethMap
        stateData={stateData}
        valueField="totalPaid"
        colorRgb="251,191,36"
        hoverRender={(abbr, s) => (
          <div className="space-y-0.5">
            <p className="text-white/70 text-xs font-semibold">{abbr}</p>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">total</span>
              <span className="text-gold-400 text-xs font-mono">{formatCurrency(s.totalPaid)}</span>
            </div>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">providers</span>
              <span className="text-white/60 text-xs font-mono">{formatNumber(s.providerCount)}</span>
            </div>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">payments</span>
              <span className="text-white/60 text-xs font-mono">{formatNumber(s.paymentCount)}</span>
            </div>
          </div>
        )}
      />
      {/* Print-only top-5 states table */}
      <div className="hidden print:block mt-3">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>State</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Total Paid</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Providers</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Payments</th>
            </tr>
          </thead>
          <tbody>
            {top5.map((s) => (
              <tr key={s.state} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "5px 8px", color: "#111827", fontWeight: 500 }}>{s.state}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#b45309", fontFamily: "monospace" }}>{formatCurrency(s.totalPaid)}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.providerCount || 0).toLocaleString()}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.paymentCount || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="print:hidden mt-3 flex gap-x-4 gap-y-1 flex-wrap">
        {top5.map((s) => (
          <span key={s.state} className="text-white/30 text-xs">
            <span className="text-white/50">{s.state}</span> {formatCurrency(s.totalPaid)}
          </span>
        ))}
      </div>
    </div>
  );
}
// ── Ownership Geographic Map (company HQ state) ───────────────────────────────
function OwnershipGeographicMap({ stateData, listName }) {
  if (!stateData || stateData.length === 0) return null;

  function exportStates() {
    const rows = [["State", "Total Interest Value ($)", "Holder Count", "Company Count", "Total Invested ($)"]];
    [...stateData].sort((a, b) => b.totalValue - a.totalValue).forEach((s) => {
      rows.push([s.state, s.totalValue.toFixed(2), s.holderCount, s.companyCount, s.totalInvested.toFixed(2)]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_geographic_ownership.csv`);
  }

  const top5 = [...stateData].sort((a, b) => b.totalValue - a.totalValue).slice(0, 5);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-white/60 text-xs uppercase tracking-widest">
            Geographic interest value distribution
          </h2>
          <p className="text-white/25 text-xs mt-0.5">By company HQ state</p>
        </div>
        <ExportButton onClick={exportStates} />
      </div>
      <ChoroplethMap
        stateData={stateData}
        valueField="totalValue"
        colorRgb="245,158,11"
        hoverRender={(abbr, s) => (
          <div className="space-y-0.5">
            <p className="text-white/70 text-xs font-semibold">{abbr}</p>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">value</span>
              <span className="text-amber-400 text-xs font-mono">{formatCurrency(s.totalValue)}</span>
            </div>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">holders</span>
              <span className="text-white/60 text-xs font-mono">{formatNumber(s.holderCount)}</span>
            </div>
            <div className="flex items-baseline justify-end gap-2">
              <span className="text-white/30 text-xs">companies</span>
              <span className="text-white/60 text-xs font-mono">{formatNumber(s.companyCount)}</span>
            </div>
          </div>
        )}
      />
      {/* Print-only top-5 states table */}
      <div className="hidden print:block mt-3">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ textAlign: "left", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>State</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Interest Value</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Holders</th>
              <th style={{ textAlign: "right", padding: "4px 8px", color: "#6b7280", fontWeight: 600 }}>Companies</th>
            </tr>
          </thead>
          <tbody>
            {top5.map((s) => (
              <tr key={s.state} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "5px 8px", color: "#111827", fontWeight: 500 }}>{s.state}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#b45309", fontFamily: "monospace" }}>{formatCurrency(s.totalValue)}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.holderCount || 0).toLocaleString()}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", color: "#374151" }}>{(s.companyCount || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="print:hidden mt-3 flex gap-x-4 gap-y-1 flex-wrap">
        {top5.map((s) => (
          <span key={s.state} className="text-white/30 text-xs">
            <span className="text-white/50">{s.state}</span> {formatCurrency(s.totalValue)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Multi-select company dropdown (used by Ownership value-by-year chart) ─────
function MultiCompanyDropdown({ companies, selected, onChange, accentColor = "amber" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredCompanies = companies.filter((c) =>
    c.companyName.toLowerCase().includes(search.toLowerCase())
  );

  const accentClass = accentColor === "amber" ? "amber" : "teal";
  const allSelected = selected === "ALL";

  function toggleCompany(name) {
    if (allSelected) {
      onChange([name]);
      return;
    }
    const current = Array.isArray(selected) ? selected : [];
    if (current.includes(name)) {
      const next = current.filter((c) => c !== name);
      onChange(next.length === 0 ? "ALL" : next);
    } else {
      onChange([...current, name]);
    }
  }

  function selectAll() {
    onChange("ALL");
    setSearch("");
  }

  const buttonLabel = allSelected
    ? `All companies (${companies.length})`
    : selected.length === 1
    ? selected[0].length > 24 ? selected[0].slice(0, 24) + "…" : selected[0]
    : `${selected.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className={`bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-${accentClass}-500/50 flex items-center gap-1.5 min-w-[180px] justify-between`}>
        <span className="truncate">{buttonLabel}</span>
        <span className="text-white/40 shrink-0">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-navy-900 border border-white/15 rounded-lg shadow-xl z-30 w-72 max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-white/10">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search companies..." autoFocus
              className="w-full bg-white/5 border border-white/10 text-white text-xs rounded-md px-2 py-1.5 focus:outline-none focus:border-white/30" />
          </div>
          <div className="p-1 border-b border-white/10">
            <button onClick={selectAll}
              className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white/5 ${allSelected ? `text-${accentClass}-400` : "text-white/60"}`}>
              {allSelected ? "✓ " : "  "}All companies ({companies.length})
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredCompanies.map((c) => {
              const isSelected = !allSelected && Array.isArray(selected) && selected.includes(c.companyName);
              return (
                <button key={c.companyName} onClick={() => toggleCompany(c.companyName)}
                  className={`w-full text-left text-xs px-3 py-1.5 hover:bg-white/5 flex items-center justify-between gap-2 ${isSelected ? `text-${accentClass}-400` : "text-white/60"}`}>
                  <span className="truncate">{isSelected ? "✓ " : "  "}{c.companyName}</span>
                  <span className="text-white/25 shrink-0">{formatCompact(c.grandTotal)}</span>
                </button>
              );
            })}
            {filteredCompanies.length === 0 && (
              <p className="text-white/30 text-xs px-3 py-3 text-center">No companies match.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ownership records with provider filter ────────────────────────────────────
function OwnershipRecordsList({ detail, listName, podNpis = new Map(), dualNpis = new Set(), tripleNpis = new Set(), excludedNpis = new Map() }) {
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("any"); // any | active | historical
  const [familyFilter, setFamilyFilter] = useState("any"); // any | family | physician
  const [podFilter, setPodFilter] = useState("any");       // any | yes | no
  const [minValue, setMinValue] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const uniqueStates = useMemo(() => {
    const set = new Set();
    detail.forEach((r) => r.state && set.add(r.state));
    return [...set].sort();
  }, [detail]);

  const uniqueSpecialties = useMemo(() => {
    const set = new Set();
    detail.forEach((r) => r.specialty && set.add(shortSpecialty(r.specialty)));
    return [...set].sort();
  }, [detail]);

  const filtered = useMemo(() => {
    return detail.filter((r) => {
      const fullName = `${r.firstName} ${r.lastName}`.trim().toLowerCase();
      const company = (r.companyName || "").toLowerCase();
      const npi = String(r.npi || "");
      if (search) {
        const q = search.toLowerCase();
        if (!fullName.includes(q) && !company.includes(q) && !npi.includes(q)) return false;
      }
      if (stateFilter && r.state !== stateFilter) return false;
      if (specialtyFilter && shortSpecialty(r.specialty) !== specialtyFilter) return false;
      if (activeFilter === "active" && !r.hasActiveOwnership) return false;
      if (activeFilter === "historical" && r.hasActiveOwnership) return false;
      const ihLower = (r.interestHolder || "").toLowerCase();
      const isFamily = ihLower.includes("family") || ihLower.includes("immediate");
      if (familyFilter === "family" && !isFamily) return false;
      if (familyFilter === "physician" && isFamily) return false;
      const min = parseFloat(minValue) || 0;
      if (min > 0 && (r.valueOfInterest || 0) < min) return false;
      if (podFilter === "yes" && !podNpis.has(r.npi)) return false;
      if (podFilter === "no" && podNpis.has(r.npi)) return false;
      return true;
    });
  }, [detail, search, stateFilter, specialtyFilter, activeFilter, familyFilter, minValue, podFilter, podNpis]);

  function exportOwnership() {
    const rows = [["NPI", "Name", "Specialty", "City", "State", "Company", "Company ID", "Interest Holder", "Terms of Interest",
      "Interest Value ($)", "Amount Invested ($)", "Active", "First Year", "Latest Year", "POD Risk (OIG Work Plan 2026)"]];
    filtered.forEach((r) => {
      rows.push([r.npi, `${r.firstName} ${r.lastName}`.trim(), r.specialty, r.city, r.state,
        r.companyName, r.companyId ? `'${r.companyId}` : "",
        r.interestHolder, r.termsOfInterest,
        (r.valueOfInterest || 0).toFixed(2), (r.amountInvested || 0).toFixed(2),
        r.hasActiveOwnership ? "Yes" : "No", r.firstYear, r.latestYear,
        podNpis.has(r.npi) ? (podNpis.get(r.npi) ? `Tier ${podNpis.get(r.npi)}` : "Tier 1") : "No"]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_ownership_records.csv`);
  }

  function resetFilters() {
    setSearch(""); setStateFilter(""); setSpecialtyFilter("");
    setActiveFilter("any"); setFamilyFilter("any"); setPodFilter("any"); setMinValue("");
  }

  const hasFilters = search || stateFilter || specialtyFilter ||
    activeFilter !== "any" || familyFilter !== "any" || podFilter !== "any" || minValue;

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">Ownership records</h2>
        <ExportButton onClick={exportOwnership} />
      </div>

      <div className="print:hidden flex items-center gap-2 mb-3">
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, NPI, or company..."
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-amber-500/50" />
        <button onClick={() => setFiltersOpen(!filtersOpen)}
          className={`text-xs px-3 py-2 rounded-lg border transition-all ${
            filtersOpen || hasFilters ? "border-amber-500/40 text-amber-400 bg-amber-500/10" : "border-white/10 text-white/40 hover:border-white/20"
          }`}>
          ⚙ Filters {hasFilters && "•"}
        </button>
        {hasFilters && <button onClick={resetFilters} className="text-white/30 hover:text-white/60 text-xs">Clear</button>}
      </div>

      {filtersOpen && (
        <div className="bg-white/[0.02] border border-white/5 rounded-lg p-3 mb-4 grid grid-cols-2 md:grid-cols-3 gap-2">
          <div>
            <label className="text-white/40 text-xs mb-1 block">State</label>
            <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50">
              <option value="">Any</option>
              {uniqueStates.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Specialty</label>
            <select value={specialtyFilter} onChange={(e) => setSpecialtyFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50">
              <option value="">Any</option>
              {uniqueSpecialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Min interest value ($)</label>
            <input type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)}
              placeholder="e.g. 100000"
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50" />
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Status</label>
            <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50">
              <option value="any">Any</option>
              <option value="active">Active 2025</option>
              <option value="historical">Historical only</option>
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">Interest holder</label>
            <select value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50">
              <option value="any">Any</option>
              <option value="physician">Physician only</option>
              <option value="family">Family member only</option>
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs mb-1 block">POD Risk</label>
            <select value={podFilter} onChange={(e) => setPodFilter(e.target.value)}
              className="w-full bg-navy-700 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/50">
              <option value="any">Any</option>
              <option value="yes">POD Risk only</option>
              <option value="no">No POD Risk</option>
            </select>
          </div>
        </div>
      )}

      <p className="print:hidden text-white/30 text-xs mb-3">
        Showing {Math.min(filtered.length, 20)} of {formatNumber(filtered.length)} record{filtered.length !== 1 ? "s" : ""}
        {hasFilters && detail.length !== filtered.length && (
          <span className="text-white/20"> · filtered from {formatNumber(detail.length)}</span>
        )}
      </p>

      {/* Print-only top-5 ownership records */}
      <div className="hidden print:block mb-4">
        <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">
          Top Ownership Records
        </h2>
        <div className="space-y-2">
          {detail.slice(0, 5).map((r, i) => {
            const isFamily = (r.interestHolder || "").toLowerCase().includes("family") ||
                              (r.interestHolder || "").toLowerCase().includes("immediate");
            return (
              <div key={i} className={`border rounded-lg px-4 py-3 ${r.hasActiveOwnership ? "border-amber-200 bg-amber-50" : "border-gray-200"}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-gray-900 text-sm font-medium">{`${r.firstName} ${r.lastName}`.trim() || r.npi}</p>
                      {r.hasActiveOwnership && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active 2025</span>}
                      {isFamily && <span className="text-xs text-violet-600 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-full">Family member</span>}
                    </div>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {r.specialty && <span>{shortSpecialty(r.specialty)} · </span>}
                      {r.companyName} · {r.firstYear === r.latestYear ? r.firstYear : `${r.firstYear}–${r.latestYear}`}
                    </p>
                    {r.termsOfInterest && <p className="text-gray-400 text-xs mt-0.5 italic">{r.termsOfInterest}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-amber-700 text-sm font-mono font-medium">{formatCurrency(r.valueOfInterest || 0)}</p>
                    <p className="text-gray-400 text-xs">interest value</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {detail.length > 5 && (
          <p className="text-gray-400 text-xs mt-2">Showing 5 of {formatNumber(detail.length)} records · export full list from ProviderPayWatch</p>
        )}
      </div>

      <div className="print:hidden space-y-2">
        {filtered.slice(0, 20).map((r, i) => {
          const isFamily = (r.interestHolder || "").toLowerCase().includes("family") ||
                            (r.interestHolder || "").toLowerCase().includes("immediate");
          return (
            <div key={i} className={`rounded-lg border px-3 py-2.5 ${
              r.hasActiveOwnership ? "bg-amber-500/5 border-amber-500/20" : "bg-white/[0.02] border-white/[0.06]"
            }`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/physician/${r.npi}?name=${encodeURIComponent(`${r.firstName} ${r.lastName}`.trim())}&state=${encodeURIComponent(r.state || "")}`}
                      className="text-white/80 text-xs font-medium hover:text-amber-300 transition-colors">
                      {`${r.firstName} ${r.lastName}`.trim() || r.npi}
                    </Link>
                    {r.hasActiveOwnership && (
                      <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">Active 2025</span>
                    )}
                    {isFamily && (
                      <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full shrink-0">Family member</span>
                    )}
                    {tripleNpis.has(r.npi) && (
                      <span className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚠ Triple Interest</span>
                    )}
                    {!tripleNpis.has(r.npi) && dualNpis.has(r.npi) && (
                      <span className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚑ Dual Interest</span>
                    )}
                    {(excludedNpis.get(r.npi)?.oig || excludedNpis.get(r.npi)?.sam) && (
                      <span className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚠ Excluded</span>
                    )}
                    {podNpis.has(r.npi) && (
                      <span className="bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                        ⚠ POD Risk Tier {podNpis.get(r.npi) || 1}
                      </span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs mt-0.5">
                    {r.specialty && <span>{shortSpecialty(r.specialty)} · </span>}
                    {r.companyId ? (
                      <Link to={`/company/${r.companyId}`} className="hover:text-amber-300 transition-colors">{r.companyName}</Link>
                    ) : r.companyName}
                    {" · "}{r.firstYear === r.latestYear ? r.firstYear : `${r.firstYear}–${r.latestYear}`}
                  </p>
                  {r.termsOfInterest && <p className="text-white/25 text-xs mt-0.5 italic line-clamp-1">{r.termsOfInterest}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-amber-400 text-xs font-mono">{formatCurrency(r.valueOfInterest || 0)}</p>
                  <p className="text-white/25 text-xs">interest value</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length > 20 && (
        <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
          Showing 20 of {formatNumber(filtered.length)} records · export for full list
        </p>
      )}
      {filtered.length === 0 && (
        <p className="text-white/30 text-xs text-center py-4">No records match the current filters.</p>
      )}
    </div>
  );
}

// ── Ownership Value by Year line chart with hover tooltip ─────────────────────
function OwnershipValueByYearChart({ trendCompanyList, selectedCompanies, listName }) {
  const [hoveredYear, setHoveredYear] = useState(null);

  // Resolve selection
  const activeSet = selectedCompanies === "ALL"
    ? new Set(trendCompanyList.map((c) => c.companyName))
    : new Set(selectedCompanies);

  // Compute year totals from selected companies
  const yearMap = {};
  ALL_YEARS.forEach((y) => (yearMap[y] = 0));
  trendCompanyList.forEach((c) => {
    if (!activeSet.has(c.companyName)) return;
    (c.byYear || []).forEach((row) => {
      if (yearMap[row.year] !== undefined) yearMap[row.year] += row.total || 0;
    });
  });
  const lineYears = ALL_YEARS.map((y) => ({ year: y, value: yearMap[y] }));
  const maxLineValue = Math.max(...lineYears.map((d) => d.value), 1);

  function exportValueByYear() {
    // Pivot: one row per company, ALL companies regardless of filter,
    // with company_name and company_id in columns 1-2, then one column
    // per year, then grand total — matches General tab trend export format.
    const yearTotals = {};
    ALL_YEARS.forEach((y) => (yearTotals[y] = 0));

    const rows = [
      ["Company Name", "Company ID", ...ALL_YEARS.map((y) => String(y)), "Grand Total"],
    ];

    const sortedCompanies = [...trendCompanyList].sort((a, b) => (b.grandTotal || 0) - (a.grandTotal || 0));

    sortedCompanies.forEach((c) => {
      const yearMap = {};
      ALL_YEARS.forEach((y) => (yearMap[y] = 0));
      (c.byYear || []).forEach((r) => {
        if (yearMap[r.year] !== undefined) {
          yearMap[r.year] = r.total || 0;
          yearTotals[r.year] += r.total || 0;
        }
      });
      rows.push([
        c.companyName,
        c.companyId ? `'${c.companyId}` : "",
        ...ALL_YEARS.map((y) => yearMap[y].toFixed(2)),
        (c.grandTotal || 0).toFixed(2),
      ]);
    });

    const grandSum = Object.values(yearTotals).reduce((s, v) => s + v, 0);
    rows.push([
      "TOTAL",
      "",
      ...ALL_YEARS.map((y) => yearTotals[y].toFixed(2)),
      grandSum.toFixed(2),
    ]);

    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_ownership_value_by_year.csv`);
  }

  // SVG point coordinates — used by both line and hover targets
  const W = 600, H = 160, padL = 30, padR = 30, padT = 20, padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const points = lineYears.map((d, i) => ({
    ...d,
    x: padL + (i / (lineYears.length - 1)) * innerW,
    y: padT + innerH - (d.value / maxLineValue) * innerH,
  }));

  const hov = hoveredYear ? points.find((p) => p.year === hoveredYear) : null;

  return (
    <>
      <div className="relative" style={{ height: "180px" }}>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none">
          {/* Gridlines */}
          {[0.25, 0.5, 0.75].map((pct, i) => (
            <line key={i} x1={padL} y1={padT + innerH - pct * innerH} x2={W - padR} y2={padT + innerH - pct * innerH}
              stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          ))}
          {/* Line */}
          <polyline fill="none" stroke="rgba(245,158,11,0.85)" strokeWidth="2"
            points={points.map((p) => `${p.x},${p.y}`).join(" ")} />
          {/* Area fill */}
          <polygon
            fill="rgba(245,158,11,0.08)"
            points={`${padL},${padT + innerH} ${points.map((p) => `${p.x},${p.y}`).join(" ")} ${W - padR},${padT + innerH}`}
          />
          {/* Hover targets — wide invisible rects for easier hovering */}
          {points.map((p, i) => {
            const segW = innerW / (lineYears.length - 1);
            return (
              <rect key={`hover-${p.year}`}
                x={p.x - segW / 2} y={padT}
                width={segW} height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoveredYear(p.year)}
                onMouseLeave={() => setHoveredYear(null)}
                style={{ cursor: "pointer" }}
              />
            );
          })}
          {/* Points + data labels */}
          {points.map((p) => (
            <g key={p.year}>
              {p.value > 0 && (
                <text
                  x={p.x}
                  y={p.y - 10}
                  textAnchor="middle"
                  style={{
                    fontSize: "9px",
                    fill: hoveredYear === p.year ? "rgba(245,158,11,1)" : "rgba(255,255,255,0.35)",
                    fontWeight: hoveredYear === p.year ? 700 : 400,
                    transition: "fill 0.15s",
                    pointerEvents: "none",
                  }}
                >
                  {formatCompact(p.value)}
                </text>
              )}
              <circle cx={p.x} cy={p.y} r={hoveredYear === p.year ? 5 : 3.5}
                fill="rgba(245,158,11,1)"
                style={{ transition: "r 0.15s", pointerEvents: "none" }} />
            </g>
          ))}
          {/* X-axis year labels */}
          {points.map((p) => (
            <text key={`lbl-${p.year}`} x={p.x} y={H - 8} textAnchor="middle"
              style={{ fontSize: "10px", fill: hoveredYear === p.year ? "rgba(245,158,11,1)" : "rgba(255,255,255,0.3)", transition: "fill 0.15s" }}>
              {p.year}
            </text>
          ))}
          {/* Hover tooltip */}
          {hov && (
            <g pointerEvents="none">
              <line x1={hov.x} y1={padT} x2={hov.x} y2={padT + innerH}
                stroke="rgba(245,158,11,0.3)" strokeWidth="1" strokeDasharray="3 3" />
              {/* Tooltip rectangle */}
              <g transform={`translate(${Math.min(hov.x + 10, W - 130)}, ${Math.max(hov.y - 36, padT)})`}>
                <rect x="0" y="0" width="120" height="36" rx="6"
                  fill="rgba(15,23,42,0.95)" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
                <text x="8" y="14" style={{ fontSize: "10px", fill: "rgba(255,255,255,0.5)" }}>{hov.year}</text>
                <text x="8" y="28" style={{ fontSize: "11px", fontWeight: 600, fill: "rgba(245,158,11,1)" }}>{formatCurrency(hov.value)}</text>
              </g>
            </g>
          )}
        </svg>
      </div>
      <div className="flex justify-end mt-2">
        <ExportButton onClick={exportValueByYear} />
      </div>
    </>
  );
}

// ── Ownership Tab ─────────────────────────────────────────────────────────────
function OwnershipTab({ userId, listId, listName }) {
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [detail, setDetail] = useState([]);
  const [trend, setTrend] = useState([]);
  const [stateBreakdown, setStateBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trendCompanyFilter, setTrendCompanyFilter] = useState("ALL");
  const [podNpis, setPodNpis] = useState(new Map());
  const [dualNpis, setDualNpis] = useState(new Set());
  const [tripleNpis, setTripleNpis] = useState(new Set());
  const [excludedNpis, setExcludedNpis] = useState(new Map());

  useEffect(() => {
    if (!userId || !listId) return;
    setLoading(true);
    Promise.all([
      getListOwnershipSummary(userId, listId),
      getListAllOwnershipCompanies(userId, listId),
      getListOwnershipDetail(userId, listId),
      getListOwnershipTrend(userId, listId),
      getListOwnershipStateBreakdown(userId, listId),
    ]).then(([s, c, d, t, geo]) => {
      setSummary(s);
      setCompanies(c || []);
      setDetail(d || []);
      setTrend(t || []);
      setStateBreakdown(geo || []);
      // Fetch POD, dual interest, and exclusion flags for ownership NPIs
      const uniqueNpis = [...new Set((d || []).map((r) => r.npi).filter(Boolean))];
      if (uniqueNpis.length > 0) {
        checkPodBatch(uniqueNpis)
          .then((podList) => setPodNpis(new Map(podList.map((p) => [p.npi, p.podTier]))))
          .catch(() => {});
        checkDualInterestBatch(uniqueNpis)
          .then((results) => {
            setDualNpis(new Set(results.map((r) => r.npi)));
            setTripleNpis(new Set(results.filter((r) => r.hasTripleInterest).map((r) => r.npi)));
          })
          .catch(() => {});
        checkExclusionsBatch(uniqueNpis)
          .then((results) => setExcludedNpis(new Map(results.map((e) => [e.npi, { oig: e.oig, sam: e.sam }]))))
          .catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId, listId]);

  if (loading) return <div className="text-white/30 text-sm animate-pulse py-12 text-center">Loading ownership data…</div>;
  if (!summary || summary.holderCount === 0) {
    return (
      <div className="card text-center py-12">
        <p className="text-white/50 text-sm">No ownership or investment interests found for providers in this list.</p>
      </div>
    );
  }

  // ── Active interest value — sum from detail records for accuracy ────────
  const activeInterestValue = detail
    .filter((r) => r.hasActiveOwnership)
    .reduce((s, r) => s + (r.valueOfInterest || 0), 0);

  const activeCompanies = companies.filter((c) => c.activeHolderCount > 0)
    .map((c) => ({ ...c, displayValue: c.activeValue || c.totalValue }))
    .sort((a, b) => b.displayValue - a.displayValue);

  // ── Holder type donut ──────────────────────────────────────────────────
  const holderMap = {};
  for (const r of detail) {
    let cat = "Other";
    const h = (r.interestHolder || "").toLowerCase();
    if (h.includes("physician and immediate") || h.includes("joint")) cat = "Joint";
    else if (h.includes("family") || h.includes("immediate")) cat = "Immediate Family Member";
    else if (h.includes("physician")) cat = "Physician";
    if (!holderMap[cat]) holderMap[cat] = { count: 0, value: 0 };
    holderMap[cat].count += 1;
    holderMap[cat].value += r.valueOfInterest || 0;
  }
  const holderSegments = Object.entries(holderMap)
    .map(([cat, d]) => ({ label: cat, count: d.count, value: d.value }))
    .sort((a, b) => b.count - a.count);
  const HOLDER_COLORS = {
    "Physician": "rgba(251,191,36,0.85)",
    "Immediate Family Member": "rgba(249,115,22,0.80)",
    "Joint": "rgba(139,92,246,0.85)",
    "Other": "rgba(100,116,139,0.70)",
  };
  const totalHolderCount = holderSegments.reduce((s, x) => s + x.count, 0);

  const cx = 60, cy = 60, R = 48, r = 30;
  let runAngle = -Math.PI / 2;
  const donutPaths = holderSegments.map((seg) => {
    const angle = (seg.count / totalHolderCount) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(runAngle), y1 = cy + R * Math.sin(runAngle);
    runAngle += angle;
    const x2 = cx + R * Math.cos(runAngle), y2 = cy + R * Math.sin(runAngle);
    const ix1 = cx + r * Math.cos(runAngle - angle), iy1 = cy + r * Math.sin(runAngle - angle);
    const ix2 = cx + r * Math.cos(runAngle), iy2 = cy + r * Math.sin(runAngle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r} ${r} 0 ${large} 0 ${ix1} ${iy1} Z`;
    return { ...seg, d };
  });

  const valueByHolder = holderSegments.slice().sort((a, b) => b.value - a.value);
  const maxHolderValue = Math.max(...valueByHolder.map((s) => s.value), 1);
  const totalHolderValue = valueByHolder.reduce((s, x) => s + x.value, 0);

  // ── Equity holders by year ─────────────────────────────────────────────
  // Track Sets of NPIs per year to avoid double-counting providers
  // who hold equity in multiple companies (one detail row per NPI+company).
  const yearNpiSets = {};
  const activeNpiSet = new Set();
  for (const r of detail) {
    const first = r.firstYear || 2016;
    const last = r.latestYear || 2025;
    for (let y = first; y <= last; y++) {
      if (y < 2016 || y > 2025) continue;
      if (!yearNpiSets[y]) yearNpiSets[y] = new Set();
      yearNpiSets[y].add(r.npi);
    }
    if (r.hasActiveOwnership) activeNpiSet.add(r.npi);
  }
  const yearData = ALL_YEARS.map((y) => ({
    year: y,
    total: yearNpiSets[y]?.size || 0,
    active: y === 2025 ? activeNpiSet.size : 0,
  }));
  const maxYearCount = Math.max(...yearData.map((d) => d.total), 1);

  // ── Value-by-year company list — sorted, includes ALL companies ────────
  const trendCompanyList = [...trend].sort((a, b) => b.grandTotal - a.grandTotal);

  // ── Interest type categorization ───────────────────────────────────────
  const interestTypeMap = {};
  for (const row of detail) {
    if (!row.termsOfInterest) continue;
    const cat = categorize(row.termsOfInterest);
    interestTypeMap[cat] = (interestTypeMap[cat] || 0) + (row.valueOfInterest || 0);
  }
  const interestTypes = Object.entries(interestTypeMap).sort((a, b) => b[1] - a[1]);
  const maxTypeVal = Math.max(...interestTypes.map(([, v]) => v), 1);

  function exportInterestTypes() {
    const rows = [["Interest Type", "Total Value ($)"]];
    interestTypes.forEach(([cat, v]) => rows.push([cat, v.toFixed(2)]));
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_interest_types.csv`);
  }
  function exportHolderDonut() {
    const rows = [["Holder Type", "Holder Count", "Total Value ($)"]];
    holderSegments.forEach((s) => rows.push([s.label, s.count, s.value.toFixed(2)]));
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_holder_types.csv`);
  }
  function exportValueByHolder() {
    const rows = [["Holder Type", "Total Value ($)", "% of Total"]];
    valueByHolder.forEach((s) => rows.push([s.label, s.value.toFixed(2),
      ((s.value / totalHolderValue) * 100).toFixed(1) + "%"]));
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_value_by_holder.csv`);
  }
  function exportHoldersByYear() {
    const rows = [["Year", "Total Holders", "Active 2025"]];
    yearData.forEach((d) => rows.push([d.year, d.total, d.active]));
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_holders_by_year.csv`);
  }
  function exportOwnershipCompanies() {
    const rows = [["Company", "Company ID", "Active Holders", "Active Value ($)", "Total Holders", "Total Value ($)"]];
    activeCompanies.forEach((c) => {
      rows.push([c.companyName, c.companyId ? `'${c.companyId}` : "",
        c.activeHolderCount || 0, (c.activeValue || 0).toFixed(2),
        c.holderCount, (c.totalValue || 0).toFixed(2)]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_ownership_companies.csv`);
  }

  return (
    <div>
      {/* Stat cards */}
      <div className="flex gap-3 flex-wrap mb-6">
        <StatCard value={formatCompact(activeInterestValue)} label="active interest value" color="text-amber-400" />
        <StatCard value={formatNumber(summary.activeHolderCount)} label="active holders (2025)" color="text-green-400" />
        {summary.familyHolderCount > 0 && (
          <StatCard value={formatNumber(summary.familyHolderCount)} label="family member interests" color="text-violet-400" sub="Stark Law relevant" />
        )}
      </div>

      {/* 1. Ownership value by year — line chart with multi-select */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="text-white/60 text-xs uppercase tracking-widest">Ownership value by year</h2>
          <div className="print:hidden">
            <MultiCompanyDropdown
              companies={trendCompanyList}
              selected={trendCompanyFilter}
              onChange={setTrendCompanyFilter}
              accentColor="amber"
            />
          </div>
        </div>
        <OwnershipValueByYearChart
          trendCompanyList={trendCompanyList}
          selectedCompanies={trendCompanyFilter}
          listName={listName}
        />
      </div>

      {/* 2. Equity holders by year */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white/60 text-xs uppercase tracking-widest">Equity holders by year</h2>
          <ExportButton onClick={exportHoldersByYear} />
        </div>
        <div className="relative" style={{ height: "140px" }}>
          <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-4" style={{ left: "30px" }}>
            {yearData.map(({ year, total, active }) => {
              const heightPct = (total / maxYearCount) * 100;
              return (
                <div key={year} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                  {total > 0 && (
                    <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "2px" }}>
                      <span className="text-white/40" style={{ fontSize: "9px" }}>{formatNumber(total)}</span>
                    </div>
                  )}
                  <div className="w-full rounded-t overflow-hidden flex flex-col-reverse" style={{ height: total > 0 ? `${Math.max(heightPct, 3)}%` : "3px" }}>
                    {active > 0 && (
                      <div style={{ height: `${(active / total) * 100}%`, backgroundColor: "rgba(34,197,94,0.70)" }} />
                    )}
                    <div style={{ height: `${((total - active) / total) * 100}%`, backgroundColor: "rgba(251,191,36,0.65)" }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="absolute bottom-0 left-0 right-0 flex gap-1.5" style={{ paddingLeft: "32px" }}>
            {ALL_YEARS.map((y) => (
              <div key={y} className="flex-1 text-center"><span className="text-white/25" style={{ fontSize: "10px" }}>{y}</span></div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "rgba(251,191,36,0.65)" }} />
            <span className="text-white/30 text-xs">Prior years</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "rgba(34,197,94,0.70)" }} />
            <span className="text-white/30 text-xs">Active 2025</span>
          </div>
        </div>
      </div>

      {/* 3. Holder type donut + Value by holder side-by-side */}
      {holderSegments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white/60 text-xs uppercase tracking-widest">Interest holder type</h2>
              <ExportButton onClick={exportHolderDonut} />
            </div>
            <div className="flex items-center gap-4">
              <div className="shrink-0" style={{ width: 120, height: 120 }}>
                <svg viewBox="0 0 120 120" width="120" height="120">
                  {donutPaths.map((seg) => (
                    <path key={seg.label} d={seg.d}
                      fill={HOLDER_COLORS[seg.label] || HOLDER_COLORS.Other}
                      stroke="rgba(15,23,42,0.6)" strokeWidth="1" />
                  ))}
                  <text x="60" y="56" textAnchor="middle" style={{ fontSize: "14px", fontWeight: 700, fill: "rgba(255,255,255,0.85)" }}>{formatNumber(totalHolderCount)}</text>
                  <text x="60" y="68" textAnchor="middle" style={{ fontSize: "7px", fill: "rgba(255,255,255,0.35)" }}>interests</text>
                </svg>
              </div>
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                {holderSegments.map((seg) => (
                  <div key={seg.label} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: HOLDER_COLORS[seg.label] || HOLDER_COLORS.Other }} />
                      <span className="text-white/50 text-xs truncate">{seg.label}</span>
                    </div>
                    <span className="text-white/70 text-xs font-medium shrink-0">{formatNumber(seg.count)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white/60 text-xs uppercase tracking-widest">Interest value by holder</h2>
              <ExportButton onClick={exportValueByHolder} />
            </div>
            <div className="space-y-3">
              {valueByHolder.map((seg) => {
                const pct = (seg.value / maxHolderValue) * 100;
                const sharePct = totalHolderValue > 0 ? (seg.value / totalHolderValue) * 100 : 0;
                return (
                  <div key={seg.label}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: HOLDER_COLORS[seg.label] || HOLDER_COLORS.Other }} />
                        <span className="text-white/60 text-xs truncate">{seg.label}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-white/25 text-xs">{sharePct.toFixed(1)}%</span>
                        <span className="text-amber-400 text-xs font-mono w-20 text-right">{formatCurrency(seg.value)}</span>
                      </div>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: HOLDER_COLORS[seg.label] || HOLDER_COLORS.Other }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 4. Interest type breakdown */}
      {interestTypes.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/60 text-xs uppercase tracking-widest">Interest type breakdown</h2>
            <ExportButton onClick={exportInterestTypes} />
          </div>
          <div className="space-y-3">
            {interestTypes.map(([cat, val]) => (
              <div key={cat}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white/60 text-xs">{cat}</span>
                  <span className="text-amber-400 text-xs font-mono">{formatCurrency(val)}</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-2">
                  <div className="h-2 rounded-full bg-amber-400/60" style={{ width: `${(val / maxTypeVal) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 5. Top companies — active holders */}
      {activeCompanies.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/60 text-xs uppercase tracking-widest">Top companies by active interest value</h2>
            <ExportButton onClick={exportOwnershipCompanies} />
          </div>
          <div className="space-y-3">
            {activeCompanies.slice(0, 10).map((c, i) => {
              const max = activeCompanies[0]?.displayValue || 1;
              const pct = ((c.displayValue || 0) / max) * 100;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-white/20 text-xs w-5 shrink-0">{i + 1}</span>
                      {c.companyId ? (
                        <Link to={`/company/${c.companyId}`}
                          className="text-white/70 hover:text-amber-300 text-xs truncate transition-colors">
                          {c.companyName}
                        </Link>
                      ) : (
                        <span className="text-white/70 text-xs truncate">{c.companyName}</span>
                      )}
                      <span className="text-green-400/60 text-xs shrink-0">{formatNumber(c.activeHolderCount)} active holders</span>
                    </div>
                    <span className="text-amber-400 text-xs font-mono shrink-0">{formatCurrency(c.displayValue)}</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-amber-400/50" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {activeCompanies.length > 10 && (
            <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
              Showing top 10 of {formatNumber(activeCompanies.length)} companies with active holders · export for full list
            </p>
          )}
        </div>
      )}

      {/* 6. Geographic value-of-interest map (above records) */}
      {stateBreakdown.length > 0 && (
        <OwnershipGeographicMap stateData={stateBreakdown} listName={listName} />
      )}

      {/* 7. Ownership records with provider filter */}
      {detail.length > 0 && (
        <OwnershipRecordsList
          detail={detail}
          listName={listName}
          podNpis={podNpis}
          dualNpis={dualNpis}
          tripleNpis={tripleNpis}
          excludedNpis={excludedNpis}
        />
      )}
    </div>
  );
}

// ── Research Tab ──────────────────────────────────────────────────────────────
function ResearchTab({ userId, listId, listName }) {
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [summary, setSummary] = useState(null);
  const [summaryAllYears, setSummaryAllYears] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !listId) return;
    getListResearchSummary(userId, listId, "all").then(setSummaryAllYears).catch(() => {});
    getListResearchTrend(userId, listId).then((t) => setTrend(t || [])).catch(() => {});
  }, [userId, listId]);

  useEffect(() => {
    if (!userId || !listId) return;
    setLoading(true);
    Promise.all([
      getListResearchSummary(userId, listId, selectedYear),
      getListAllResearchCompanies(userId, listId, selectedYear),
      getListResearchProviders(userId, listId, selectedYear),
      getListResearchProducts(userId, listId, selectedYear),
    ]).then(([s, c, p, prod]) => {
      setSummary(s);
      setCompanies(c || []);
      setProviders(p || []);
      setProducts(prod || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId, listId, selectedYear]);

  if (loading && !summaryAllYears) {
    return <div className="text-white/30 text-sm animate-pulse py-12 text-center">Loading research data…</div>;
  }
  if ((!summaryAllYears || summaryAllYears.investigatorCount === 0)) {
    return (
      <div className="card text-center py-12">
        <p className="text-white/50 text-sm">No research payments found for providers in this list.</p>
      </div>
    );
  }

  // ── Research funding trend — top 8 + "Other" bucket ────────────────────
  // Year-aware: top 8 reflects the selected year so chart matches Top Sponsoring
  // Companies table. Research tab always has a year selected (no "all" option).
  const allSorted = [...trend].sort((a, b) => {
    const aYear = (a.byYear || []).find((r) => r.year === selectedYear)?.total || 0;
    const bYear = (b.byYear || []).find((r) => r.year === selectedYear)?.total || 0;
    return bYear - aYear;
  });
  const top8 = allSorted.slice(0, 8);
  const rest = allSorted.slice(8);
  const COLORS = [
    "rgba(20,184,166,0.8)", "rgba(139,92,246,0.8)", "rgba(251,191,36,0.8)",
    "rgba(59,130,246,0.8)", "rgba(239,68,68,0.8)", "rgba(249,115,22,0.8)",
    "rgba(34,197,94,0.8)", "rgba(236,72,153,0.8)",
  ];
  const OTHER_COLOR = "rgba(100,116,139,0.6)";
  const colorMap = new Map(top8.map((c, i) => [c.companyName, COLORS[i]]));

  // Top 8 in segments; Other tracked separately for tooltip and export only
  const yearStacks = ALL_YEARS.map((y) => {
    let visibleTotal = 0;
    const segments = top8.map((company) => {
      const row = (company.byYear || []).find((r) => r.year === y);
      const val = row ? (row.total || 0) : 0;
      visibleTotal += val;
      return { company: company.companyName, value: val };
    });
    let otherTotal = 0;
    rest.forEach((company) => {
      const row = (company.byYear || []).find((r) => r.year === y);
      otherTotal += row ? (row.total || 0) : 0;
    });
    return {
      year: y,
      segments,
      visibleTotal,
      otherTotal,
      grandTotal: visibleTotal + otherTotal,
    };
  });
  const maxTotal = Math.max(...yearStacks.map((y) => y.grandTotal), 1);

  // ── Product type donut ─────────────────────────────────────────────────
  const typeMap = {};
  products.forEach((p) => {
    const t = p.productType || "Not Specified";
    if (!typeMap[t]) typeMap[t] = { amount: 0, count: 0 };
    typeMap[t].amount += p.totalAmount || 0;
    typeMap[t].count += p.investigatorCount || 0;
  });
  const typeSegments = Object.entries(typeMap)
    .map(([label, d]) => ({ label, amount: d.amount, count: d.count }))
    .sort((a, b) => b.amount - a.amount);
  const totalProductAmount = typeSegments.reduce((s, x) => s + x.amount, 0);
  const TYPE_COLORS = {
    "Drug": "rgba(139,92,246,0.85)",
    "Biological": "rgba(20,184,166,0.85)",
    "Device": "rgba(59,130,246,0.85)",
    "Medical Supply": "rgba(34,197,94,0.85)",
    "Not Specified": "rgba(100,116,139,0.70)",
  };
  const dcx = 60, dcy = 60, dR = 48, dr = 30;
  let typeAngle = -Math.PI / 2;
  const typePaths = typeSegments.map((seg) => {
    if (totalProductAmount === 0) return { ...seg, d: "" };
    const angle = (seg.amount / totalProductAmount) * 2 * Math.PI;
    const x1 = dcx + dR * Math.cos(typeAngle), y1 = dcy + dR * Math.sin(typeAngle);
    typeAngle += angle;
    const x2 = dcx + dR * Math.cos(typeAngle), y2 = dcy + dR * Math.sin(typeAngle);
    const ix1 = dcx + dr * Math.cos(typeAngle - angle), iy1 = dcy + dr * Math.sin(typeAngle - angle);
    const ix2 = dcx + dr * Math.cos(typeAngle), iy2 = dcy + dr * Math.sin(typeAngle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M ${x1} ${y1} A ${dR} ${dR} 0 ${large} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${dr} ${dr} 0 ${large} 0 ${ix1} ${iy1} Z`;
    return { ...seg, d };
  });

  const top10Products = products.slice(0, 10);
  const maxProductAmount = Math.max(...top10Products.map((p) => p.totalAmount || 0), 1);
  const activeInvestigators = summary?.investigatorCount || 0;

  function exportTrend() {
    // Pivot: one row per company, ALL companies, with company_name and company_id
    // in columns 1-2, then one column per year, then grand total.
    const yearTotals = {};
    ALL_YEARS.forEach((y) => (yearTotals[y] = 0));

    const rows = [
      ["Company Name", "Company ID", ...ALL_YEARS.map((y) => String(y)), "Grand Total"],
    ];

    const sortedCompanies = [...trend].sort((a, b) => (b.grandTotal || 0) - (a.grandTotal || 0));

    sortedCompanies.forEach((c) => {
      const yearMap = {};
      ALL_YEARS.forEach((y) => (yearMap[y] = 0));
      (c.byYear || []).forEach((r) => {
        if (yearMap[r.year] !== undefined) {
          yearMap[r.year] = r.total || 0;
          yearTotals[r.year] += r.total || 0;
        }
      });
      rows.push([
        c.companyName,
        c.companyId ? `'${c.companyId}` : "",
        ...ALL_YEARS.map((y) => yearMap[y].toFixed(2)),
        (c.grandTotal || 0).toFixed(2),
      ]);
    });

    const grandSum = Object.values(yearTotals).reduce((s, v) => s + v, 0);
    rows.push([
      "TOTAL",
      "",
      ...ALL_YEARS.map((y) => yearTotals[y].toFixed(2)),
      grandSum.toFixed(2),
    ]);

    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_research_trend.csv`);
  }
  function exportCompanies() {
    const rows = [["Rank", "Company", "Company ID", "Investigator Count", "Total Funding ($)"]];
    companies.forEach((c, i) => {
      rows.push([i + 1, c.companyName, c.companyId ? `'${c.companyId}` : "",
        c.investigatorCount, (c.totalFunding || 0).toFixed(2)]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_research_companies_${selectedYear}.csv`);
  }
  function exportProviders() {
    const rows = [["Rank", "NPI", "Name", "Specialty", "City", "State",
      "Associated Research ($)", "Direct Payments ($)", "Total Funding ($)",
      "Payment Count", "Study Count", "Company Count",
      "Preclinical", "Ownership", "Research+Ownership Dual", "Excluded",
      "POD Risk (OIG Work Plan 2026)"]];
    providers.forEach((p, i) => {
      rows.push([
        i + 1, p.npi, `${p.firstName} ${p.lastName}`.trim(), p.specialty, p.city, p.state,
        p.piAmount > 0 ? (p.piAmount || 0).toFixed(2) : "",
        p.directAmount > 0 ? (p.directAmount || 0).toFixed(2) : "",
        (p.totalFunding || 0).toFixed(2),
        p.paymentCount, p.studyCount, p.companyCount,
        p.hasPreclinical ? "Yes" : "No",
        p.hasOwnership ? "Yes" : "No",
        p.hasResearchOwnershipDualInterest ? "Yes" : "No",
        p.isExcluded ? "Yes" : "No",
        p.hasPodRisk ? (p.podTier ? `Tier ${p.podTier}` : "Tier 1") : "No",
      ]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_research_providers_${selectedYear}.csv`);
  }
  function exportProducts() {
    const rows = [["Rank", "Product", "Product Type", "Total Amount ($)", "Investigator Count", "Payment Count", "Company Count"]];
    products.forEach((p, i) => {
      rows.push([i + 1, p.productName, p.productType, (p.totalAmount || 0).toFixed(2),
        p.investigatorCount, p.paymentCount, p.companyCount]);
    });
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_research_products_${selectedYear}.csv`);
  }
  function exportProductTypes() {
    const rows = [["Product Type", "Total Amount ($)", "% of Total"]];
    typeSegments.forEach((s) => rows.push([s.label, s.amount.toFixed(2),
      ((s.amount / totalProductAmount) * 100).toFixed(1) + "%"]));
    downloadCsv(rows, `${listName.replace(/\s+/g, "_")}_research_product_types_${selectedYear}.csv`);
  }

  const totalFundingAllYears = summaryAllYears?.totalFunding || 0;
  const currentYearFunding = summary?.totalFunding || 0;

  return (
    <div>
      {/* Year selector */}
      <div className="print:hidden flex items-center justify-between mb-4">
        <p className="text-white/25 text-xs uppercase tracking-widest">Research data</p>
        <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-teal-500/60">
          {ALL_YEARS.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Stat cards */}
      <div className="flex gap-3 flex-wrap mb-6">
        <StatCard value={formatCompact(totalFundingAllYears)} label="total funding (all years)" color="text-teal-400" />
        <StatCard value={formatCompact(currentYearFunding)} label={`funding (${selectedYear})`} color="text-teal-400" />
        <StatCard value={formatNumber(activeInvestigators)} label={`active investigators (${selectedYear})`} color={activeInvestigators > 0 ? "text-green-400" : "text-white/30"} />
        <StatCard value={formatNumber(summary?.companyCount || 0)} label={`sponsoring companies (${selectedYear})`} color="text-white" />
      </div>

      {/* Research funding trend — top 8 + Other */}
      {(top8.length > 0 || rest.length > 0) && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/60 text-xs uppercase tracking-widest">Research funding trend</h2>
            <div className="flex items-center gap-3">
              <p className="text-white/25 text-xs">Top 8 by {selectedYear} funding + Other · click year to filter</p>
              <ExportButton onClick={exportTrend} />
            </div>
          </div>
          <div className="relative" style={{ height: "200px" }}>
            <div className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8" style={{ left: "30px" }}>
              {yearStacks.map(({ year, segments, visibleTotal, otherTotal, grandTotal }) => {
                const heightPct = (grandTotal / maxTotal) * 100;
                const isSelected = selectedYear === year;
                return (
                  <div key={year} className="flex-1 flex flex-col items-center justify-end h-full group relative cursor-pointer"
                    onClick={() => setSelectedYear(year)}>
                    {grandTotal > 0 && (
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none" style={{ minWidth: "200px" }}>
                        <p className="text-gold-400 font-medium mb-1.5 pb-1.5 border-b border-white/10">{year} · {formatCompact(grandTotal)}</p>
                        {segments.filter((s) => s.value > 0).sort((a, b) => b.value - a.value).map(({ company, value }) => {
                          const pct = grandTotal > 0 ? (value / grandTotal) * 100 : 0;
                          return (
                            <div key={company} className="flex items-center justify-between gap-2 mt-1">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: colorMap.get(company) }} />
                                <span className="text-white/40 truncate" style={{ maxWidth: "100px" }}>{company.length > 18 ? company.slice(0, 18) + "…" : company}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-white/25" style={{ minWidth: "32px", textAlign: "right" }}>{pct.toFixed(1)}%</span>
                                <span className="text-white/60" style={{ minWidth: "48px", textAlign: "right" }}>{formatCompact(value)}</span>
                              </div>
                            </div>
                          );
                        })}
                        {otherTotal > 0 && (
                          <div className="flex items-center justify-between gap-2 mt-1 pt-1.5 border-t border-white/5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: OTHER_COLOR }} />
                              <span className="text-white/40 italic">Other ({rest.length})</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-white/25" style={{ minWidth: "32px", textAlign: "right" }}>{(otherTotal / grandTotal * 100).toFixed(1)}%</span>
                              <span className="text-white/60" style={{ minWidth: "48px", textAlign: "right" }}>{formatCompact(otherTotal)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {grandTotal > 0 && (
                      <div className="absolute w-full text-center pointer-events-none" style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "3px" }}>
                        <span className="text-gold-400/70 font-medium" style={{ fontSize: "9px" }}>{formatCompact(grandTotal)}</span>
                      </div>
                    )}
                    <div className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                      style={{
                        height: visibleTotal > 0 ? `${Math.max(heightPct, 3)}%` : "3px",
                        outline: isSelected ? "2px solid rgba(251,191,36,0.8)" : "none",
                        outlineOffset: "2px",
                      }}>
                      {segments.map(({ company, value }) =>
                        value > 0 ? (<div key={company} style={{ height: `${(value / visibleTotal) * 100}%`, backgroundColor: colorMap.get(company), minHeight: "2px" }} />) : null
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="absolute bottom-0 left-0 right-0 flex gap-1.5" style={{ paddingLeft: "32px" }}>
              {ALL_YEARS.map((y) => (
                <div key={y} className="flex-1 text-center">
                  <span className={`${y === selectedYear ? "text-teal-400" : "text-white/25"}`} style={{ fontSize: "10px" }}>{y}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
            {top8.map((c, i) => (
              <div key={c.companyName} className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: COLORS[i], opacity: 0.8 }} />
                <span className="text-white/40 text-xs truncate max-w-32">{c.companyName.length > 22 ? c.companyName.slice(0, 22) + "…" : c.companyName}</span>
              </div>
            ))}
            {rest.length > 0 && (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: OTHER_COLOR }} />
                <span className="text-white/40 text-xs">Other ({rest.length} companies, hover to see)</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preclinical vs clinical split */}
      {summary && (summary.preclinicalFunding > 0 || summary.clinicalFunding > 0) && (
        <div className="card mb-6">
          <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">Preclinical vs. clinical research ({selectedYear})</h2>
          <div className="flex rounded-lg overflow-hidden h-3 mb-4">
            {summary.clinicalFunding > 0 && (
              <div className="bg-teal-500/70" style={{ width: `${(summary.clinicalFunding / (summary.clinicalFunding + summary.preclinicalFunding)) * 100}%` }} />
            )}
            {summary.preclinicalFunding > 0 && (
              <div className="bg-violet-500/70" style={{ width: `${(summary.preclinicalFunding / (summary.clinicalFunding + summary.preclinicalFunding)) * 100}%` }} />
            )}
          </div>
          <div className="flex gap-6 flex-wrap">
            {summary.clinicalFunding > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-sm bg-teal-500/70" />
                  <span className="text-white/50 text-xs">Clinical</span>
                </div>
                <p className="text-teal-400 text-lg font-mono font-medium">{formatCurrency(summary.clinicalFunding)}</p>
              </div>
            )}
            {summary.preclinicalFunding > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-sm bg-violet-500/70" />
                  <span className="text-white/50 text-xs">Preclinical</span>
                </div>
                <p className="text-violet-400 text-lg font-mono font-medium">{formatCurrency(summary.preclinicalFunding)}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product type donut + Top products */}
      {products.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white/60 text-xs uppercase tracking-widest">Product type ({selectedYear})</h2>
              <ExportButton onClick={exportProductTypes} />
            </div>
            <div className="flex items-center gap-4">
              <div className="shrink-0" style={{ width: 120, height: 120 }}>
                <svg viewBox="0 0 120 120" width="120" height="120">
                  {typePaths.map((seg) => (
                    <path key={seg.label} d={seg.d}
                      fill={TYPE_COLORS[seg.label] || TYPE_COLORS["Not Specified"]}
                      stroke="rgba(15,23,42,0.6)" strokeWidth="1" />
                  ))}
                  <text x="60" y="54" textAnchor="middle" style={{ fontSize: "10px", fontWeight: 700, fill: "rgba(255,255,255,0.85)" }}>
                    {formatCompact(totalProductAmount)}
                  </text>
                  <text x="60" y="66" textAnchor="middle" style={{ fontSize: "7px", fill: "rgba(255,255,255,0.35)" }}>total</text>
                </svg>
              </div>
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                {typeSegments.map((seg) => {
                  const pct = totalProductAmount > 0 ? ((seg.amount / totalProductAmount) * 100).toFixed(1) : "0.0";
                  return (
                    <div key={seg.label} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: TYPE_COLORS[seg.label] || TYPE_COLORS["Not Specified"] }} />
                        <span className="text-white/50 text-xs truncate">{seg.label}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-white/25 text-xs">{pct}%</span>
                        <span className="text-teal-400 text-xs font-mono">{formatCompact(seg.amount)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white/60 text-xs uppercase tracking-widest">Top products ({selectedYear})</h2>
              <ExportButton onClick={exportProducts} />
            </div>
            <div className="space-y-2">
              {top10Products.map((p, i) => {
                const pct = ((p.totalAmount || 0) / maxProductAmount) * 100;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="text-white/20 text-xs w-4 shrink-0">{i + 1}</span>
                        <span className="text-white/70 text-xs truncate">{p.productName}</span>
                      </div>
                      <span className="text-teal-400 text-xs font-mono shrink-0">{formatCompact(p.totalAmount || 0)}</span>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{
                        width: `${pct}%`,
                        backgroundColor: TYPE_COLORS[p.productType] || TYPE_COLORS["Not Specified"],
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {products.length > 10 && (
              <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
                Showing top 10 of {formatNumber(products.length)} products · export for full list
              </p>
            )}
          </div>
        </div>
      )}

      {/* Top sponsoring companies */}
      {companies.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/60 text-xs uppercase tracking-widest">Top sponsoring companies ({selectedYear})</h2>
            <ExportButton onClick={exportCompanies} />
          </div>
          <div className="space-y-3">
            {companies.slice(0, 10).map((c, i) => {
              const max = companies[0]?.totalFunding || 1;
              const pct = ((c.totalFunding || 0) / max) * 100;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-white/20 text-xs w-5 shrink-0">{i + 1}</span>
                      {c.companyId ? (
                        <Link to={`/company/${c.companyId}`}
                          className="text-white/70 hover:text-teal-300 text-xs truncate transition-colors">
                          {c.companyName}
                        </Link>
                      ) : (
                        <span className="text-white/70 text-xs truncate">{c.companyName}</span>
                      )}
                      <span className="text-white/25 text-xs shrink-0">{formatNumber(c.investigatorCount)} investigators</span>
                    </div>
                    <span className="text-teal-400 text-xs font-mono shrink-0">{formatCurrency(c.totalFunding || 0)}</span>
                  </div>
                  <div className="w-full bg-white/5 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-teal-400/50" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {companies.length > 10 && (
            <p className="text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
              Showing top 10 of {formatNumber(companies.length)} companies · export for full list
            </p>
          )}
        </div>
      )}

      {/* Principal investigators list with conflict pills */}
      {providers.length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white/60 text-xs uppercase tracking-widest">Research investigators ({selectedYear})</h2>
            <ExportButton onClick={exportProviders} />
          </div>

          {/* Print-only top-5 investigators */}
          <div className="hidden print:block mb-4">
            <div className="space-y-2">
              {providers.slice(0, 5).map((p, i) => (
                <div key={p.npi} className={`border rounded-lg px-4 py-3 ${p.hasActiveResearch ? "border-teal-200 bg-teal-50" : "border-gray-200"}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-gray-900 text-sm font-medium">{`${p.firstName} ${p.lastName}`.trim() || p.npi}</p>
                        {p.hasActiveResearch && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active</span>}
                        {p.hasPreclinical && <span className="text-xs text-violet-600 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-full">Preclinical</span>}
                        {p.hasResearchOwnershipDualInterest && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Research+Ownership</span>}
                        {p.isExcluded && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Excluded</span>}
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {p.specialty && <span>{shortSpecialty(p.specialty)} · </span>}
                        {p.city && <span>{p.city}{p.state ? `, ${p.state}` : ""} · </span>}
                        {formatNumber(p.studyCount)} {p.studyCount === 1 ? "study" : "studies"} · {formatNumber(p.companyCount)} {p.companyCount === 1 ? "sponsor" : "sponsors"} · NPI: {p.npi}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-teal-700 text-sm font-mono font-medium">{formatCurrency(p.totalFunding || 0)}</p>
                      <p className="text-gray-400 text-xs">{formatNumber(p.paymentCount)} payments</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {providers.length > 5 && (
              <p className="text-gray-400 text-xs mt-2">Showing 5 of {formatNumber(providers.length)} investigators · export full list from ProviderPayWatch</p>
            )}
          </div>

          <div className="print:hidden space-y-2">
            {providers.slice(0, 10).map((p, i) => (
              <div key={p.npi} className={`rounded-lg border px-3 py-2.5 ${
                p.hasResearchOwnershipDualInterest ? "bg-orange-500/5 border-orange-500/20"
                : p.isExcluded ? "bg-red-500/[0.03] border-red-500/15"
                : p.hasActiveResearch ? "bg-teal-500/[0.03] border-teal-500/15"
                : "bg-white/[0.02] border-white/[0.06]"
              }`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white/30 text-xs w-5 shrink-0">{i + 1}.</span>
                      <Link to={`/physician/${p.npi}?name=${encodeURIComponent(`${p.firstName} ${p.lastName}`.trim())}&state=${encodeURIComponent(p.state || "")}`}
                        className="text-white/80 text-sm font-medium hover:text-teal-300 transition-colors">
                        {`${p.firstName} ${p.lastName}`.trim() || p.npi}
                      </Link>
                      {p.hasActiveResearch && (
                        <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">Active 2025</span>
                      )}
                      {p.hasPreclinical && (
                        <span className="bg-violet-500/15 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full shrink-0">Preclinical</span>
                      )}
                      {p.hasResearchOwnershipDualInterest && (
                        <span className="bg-orange-500/15 border border-orange-500/30 text-orange-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚑ Research+Ownership</span>
                      )}
                      {p.isExcluded && (
                        <span className="bg-red-500/15 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">⚠ Excluded</span>
                      )}
                      {p.hasOwnership && !p.hasResearchOwnershipDualInterest && (
                        <span className="bg-amber-500/10 border border-amber-500/25 text-amber-400/90 text-xs px-1.5 py-0.5 rounded-full shrink-0">🏦 Ownership</span>
                      )}
                      {p.hasPodRisk && (
                        <span className="bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                          ⚠ POD {p.podTier ? `Tier ${p.podTier}` : "Risk"}
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs mt-1">
                      {p.specialty && <span>{shortSpecialty(p.specialty)} · </span>}
                      {p.city && <span>{p.city}{p.state ? `, ${p.state}` : ""} · </span>}
                      {!p.city && p.state && <span>{p.state} · </span>}
                      {p.studyCount > 0 && <span>{formatNumber(p.studyCount)} {p.studyCount === 1 ? "study" : "studies"} · </span>}
                      {p.companyCount > 0 && <span>{formatNumber(p.companyCount)} {p.companyCount === 1 ? "sponsor" : "sponsors"}</span>}
                    </p>
                    <p className="text-white/25 text-xs font-mono mt-0.5">NPI: {p.npi}</p>
                  </div>
                  <div className="text-right shrink-0">
                    {p.piAmount > 0 && (
                      <div className="mb-0.5">
                        <p className="text-teal-400 text-sm font-mono font-medium">{formatCurrency(p.piAmount)}</p>
                        <p className="text-white/25 text-xs">associated research</p>
                      </div>
                    )}
                    {p.directAmount > 0 && (
                      <div>
                        <p className="text-teal-300 text-sm font-mono font-medium">{formatCurrency(p.directAmount)}</p>
                        <p className="text-white/25 text-xs">direct payments</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {providers.length > 10 && (
            <p className="print:hidden text-white/20 text-xs mt-3 pt-3 border-t border-white/5">
              Showing top 10 of {formatNumber(providers.length)} investigators · export for full list
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MyProviderListProfilePage() {
  const { listId } = useParams();
  const { user, isSignedIn } = useUser();
  const { isPro } = useSubscription();

  const [activeTab, setActiveTab] = useState("general");
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);

  const [generalData, setGeneralData] = useState(null);
  const [topProviders, setTopProviders] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [companyTrend, setCompanyTrend] = useState([]);
  const [stateBreakdown, setStateBreakdown] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [generalLoading, setGeneralLoading] = useState(true);

  const [listName, setListName] = useState("Provider List");

  // Load company trend once — independent of selectedYear (chart shows all years)
  useEffect(() => {
    if (!user || !listId) return;
    getListCompanyTrend(user.id, listId).then((t) => setCompanyTrend(t || [])).catch(() => {});
  }, [user?.id, listId]);

  // Year-dependent data
  useEffect(() => {
    if (!user || !listId) return;
    setGeneralLoading(true);
    Promise.all([
      getListGeneralByYear(user.id, listId, selectedYear),
      getListTopProviders(user.id, listId, selectedYear),
      getListAllCompanies(user.id, listId, selectedYear),
      getListGeneralStateBreakdown(user.id, listId, selectedYear),
      getListTopProducts(user.id, listId, selectedYear),
    ]).then(([g, p, c, geo, prod]) => {
      setGeneralData(g);
      setTopProviders(p || []);
      setAllCompanies(c || []);
      setStateBreakdown(geo || []);
      setTopProducts(prod || []);
    }).catch(() => {}).finally(() => setGeneralLoading(false));
  }, [user?.id, listId, selectedYear]);

  useEffect(() => {
    if (!user || !listId) return;
    apiFetch(`/api/user-data?action=getProviderLists&userId=${user.id}`)
      .then((r) => r.json())
      .then((data) => {
        const list = (data.lists || []).find((l) => l.id === listId);
        if (list) setListName(list.list_name);
      })
      .catch(() => {});
  }, [user?.id, listId]);

  if (!isSignedIn || !isPro) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="card text-center py-12">
          <p className="text-white/50 text-sm">Sign in with a Pro account to view list profiles.</p>
        </div>
      </main>
    );
  }

  const tabs = [
    { id: "general", icon: "💊", label: "General Payments" },
    { id: "ownership", icon: "🏦", label: "Ownership" },
    { id: "research", icon: "🔬", label: "Research" },
  ];

  // Total payments all years — use companyTrend (independent of selectedYear)
  // Fallback to generalData if companyTrend hasn't loaded yet
  const totalPaymentsAllYears = companyTrend.length > 0
    ? companyTrend.reduce((s, c) => s + (c.grandTotal || 0), 0)
    : (generalData?.paymentByYear || []).reduce((s, y) => s + (y.total || 0), 0);

  const selectedYearData = (generalData?.paymentByYear || [])
    .find((y) => y.year === selectedYear);
  const selectedYearTotal = generalData?.totalPaid ?? selectedYearData?.total ?? 0;
  const selectedYearPaymentCount = generalData?.paymentCount ?? selectedYearData?.cnt ?? 0;
  const isAllYears = selectedYear === "all";

  return (
    <main className="max-w-4xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, header, footer, .print-hide { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card {
            background: white !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 8px !important;
            box-shadow: none !important;
            break-inside: avoid;
          }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-gold-400 { color: #b45309 !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .text-violet-400 { color: #7c3aed !important; }
          svg text { fill: #374151 !important; }

          /* Force bar/line colors to print */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(245,158,11"] { background-color: #d97706 !important; opacity: 1 !important; }
          div[style*="background-color: rgba(100,116,139"] { background-color: #475569 !important; opacity: 1 !important; }
          div[style*="rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="rgba(255,255,255,0.05"] { display: none !important; }

          /* Card background overrides */
          .bg-red-500\\/10, .bg-red-500\\/5 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10, .bg-amber-500\\/5 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10, .bg-teal-500\\/5 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-orange-500\\/5 { background: #fff7ed !important; border-color: #fdba74 !important; }

          /* Hide all interactive controls */
          select { display: none !important; }
          input { display: none !important; }
          .card button { display: none !important; }

          /* Hide export rows and footnotes */
          .card .border-t.border-white\\/5 { display: none !important; }

          /* SVG line chart — force stroke colors */
          polyline[stroke] { stroke-opacity: 1 !important; }
          circle[fill] { fill-opacity: 1 !important; }

          /* Preserve 2-column side-by-side grid layout in print */
          .grid.grid-cols-1.md\\:grid-cols-2 {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 12px !important;
          }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
              ProviderPayWatch · Provider List Profile Report
            </p>
            <h1 className="text-2xl font-bold text-gray-900">{listName}</h1>
            {generalData && (
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(generalData.providerCount)} providers · {formatNumber(generalData.providersWithPayments)} with Open Payments records
                {selectedYear !== "all" ? ` · ${selectedYear} data` : " · All years"}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Source: CMS Open Payments (2016–2025) · Data reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations.
        </p>
      </div>

      <Link to="/my-providers" className="print:hidden text-white/40 hover:text-white/70 text-sm transition-colors">
        ← My Providers
      </Link>

      <div className="mt-4 mb-6">
        <p className="print:hidden text-gold-400/60 text-xs uppercase tracking-widest mb-1">Provider List Profile</p>
        <h1 className="print:hidden font-display text-4xl text-white leading-tight">{listName}</h1>
        {generalData && (
          <p className="print:hidden text-white/30 text-xs mt-2">
            {formatNumber(generalData.providerCount)} providers · {formatNumber(generalData.providersWithPayments)} with Open Payments records
          </p>
        )}
        {/* Print button — below provider count row */}
        <div className="print:hidden mt-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
          >
            <span>⎙</span><span>Print / Save PDF</span>
          </button>
        </div>
      </div>

      <div className="print:hidden flex gap-1 bg-navy-800 border border-white/10 rounded-xl p-1 mb-6">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                isActive ? "bg-navy-700 text-white border border-white/10" : "text-white/40 hover:text-white/70"
              }`}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {activeTab === "general" && (
        <div>
          <div className="print:hidden flex items-center justify-between mb-6">
            <p className="text-white/25 text-xs uppercase tracking-widest">Payment data</p>
            <select
              value={selectedYear}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedYear(v === "all" ? "all" : parseInt(v));
              }}
              className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-gold-500/60"
            >
              <option value="all">All years</option>
              {ALL_YEARS.slice().reverse().map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {generalLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="card animate-pulse h-24" />)}
            </div>
          ) : generalData ? (
            <>
              {/* Stat cards */}
              <div className="flex gap-3 flex-wrap mb-6">
                <StatCard value={formatCompact(totalPaymentsAllYears)} label="total payments (all years)" color="text-gold-400" />
              {!isAllYears && (
                <>
                  <StatCard value={formatCompact(selectedYearTotal)} label={`payments (${selectedYear})`} color="text-gold-400" />
                  <StatCard value={formatNumber(selectedYearPaymentCount)} label={`payment records (${selectedYear})`} color="text-white" />
                </>
              )}
              {isAllYears && (
                <StatCard value={formatNumber(generalData.paymentCountAllYears || 0)} label="total payment records" color="text-white" />
              )}
              <StatCard value={formatNumber(generalData.providersWithPayments || 0)} label={`providers with payments (${isAllYears ? "all years" : selectedYear})`} color="text-white" />
              {generalData.exclusionCount > 0 && (
                <StatCard value={formatNumber(generalData.exclusionCount)} label="OIG / SAM excluded (all years)" color="text-red-400" />
              )}
              </div>

              <PaymentTrendChart trend={companyTrend} selectedYear={selectedYear}
                onYearClick={(y) => setSelectedYear(y)} listName={listName} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <TopProductsCard products={topProducts} year={selectedYear} listName={listName} />
                {generalData.specialtyBreakdown?.length > 0 && (
                  <SpecialtyBreakdown specialties={generalData.specialtyBreakdown} year={selectedYear} listName={listName} />
                )}
              </div>

              {/* Top companies — moved above Top providers */}
              <TopCompaniesTable
                companies={allCompanies.length > 0 ? allCompanies : (generalData.topCompanies || [])}
                listName={listName} year={selectedYear} />

              {/* Geographic map — above Top providers */}
              {stateBreakdown.length > 0 && (
                <GeneralGeographicMap stateData={stateBreakdown} year={selectedYear} listName={listName} />
              )}

              <TopProvidersCardList providers={topProviders} year={selectedYear} listName={listName} />
            </>
          ) : (
            <div className="card text-center py-12">
              <p className="text-white/50 text-sm">No payment data found for this list.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "ownership" && (
        <OwnershipTab userId={user.id} listId={listId} listName={listName} />
      )}

      {activeTab === "research" && (
        <ResearchTab userId={user.id} listId={listId} listName={listName} />
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments 2016–2025 · Ownership & Investment Interest · Research Payments
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8">
        Source: CMS Open Payments (2016–2025) · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}